import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";
import { useLanguage } from "@/LanguageContext";
import { languageNames } from "@/translations";
import { 
  Calendar, 
  Users, 
  FileText, 
  Send, 
  Clock, 
  MapPin,
  Music, 
  Mail, 
  Phone, 
  ChevronRight,
  Menu,
  X,
  Award,
  BookOpen,
  Mic,
  Coffee,
  Utensils,
  PartyPopper,
  ClipboardList,
  GraduationCap,
  Upload,
  Check,
  Download,
  Trash2,
  Eye,
  Shield,
  LogOut,
  Euro,
  FileSpreadsheet,
  AlertCircle,
  Plus,
  Minus,
  CheckCircle,
  XCircle,
  Hash,
  BarChart3,
  TrendingUp,
  User,
  Info,
  Quote,
  ChevronLeft
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table";
import { Badge } from "./components/ui/badge";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Countdown Component
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const { t } = useLanguage();

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(targetDate) - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex gap-3 md:gap-4" data-testid="countdown-timer">
      {[
        { value: timeLeft.days, label: t.hero.days },
        { value: timeLeft.hours, label: t.hero.hours },
        { value: timeLeft.minutes, label: t.hero.min },
        { value: timeLeft.seconds, label: t.hero.sec },
      ].map((item, index) => (
        <div key={index} className="text-center">
          <div className="countdown-box text-white font-heading text-3xl md:text-4xl font-bold">
            {String(item.value).padStart(2, "0")}
          </div>
          <span className="text-gray-400 text-xs md:text-sm mt-2 block uppercase tracking-wider">
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: t.nav.about },
    { href: "#committees", label: t.nav.committees },
    { href: "#program", label: t.nav.program },
    { href: "#submissions", label: t.nav.submissions },
    { href: "#pricing", label: t.footer.prices },
    { href: "#registration", label: t.nav.registration },
    { href: "#sponsors", label: t.nav.sponsors },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="#hero" className="flex items-center gap-2">
            <span className={`font-heading font-bold text-lg md:text-xl tracking-tight ${scrolled ? "text-secondary" : "text-white"}`}>
              CIAFD<span className="text-primary">2026</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`nav-link text-sm font-medium uppercase tracking-wider transition-colors ${
                  scrolled ? "text-secondary hover:text-primary" : "text-white/90 hover:text-white"
                }`}
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center gap-1 ml-2" data-testid="language-selector">
              {Object.keys(languageNames).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`text-xs font-bold px-2 py-1 rounded transition-colors ${
                    lang === l
                      ? "bg-primary text-white"
                      : scrolled ? "text-secondary hover:text-primary" : "text-white/70 hover:text-white"
                  }`}
                >
                  {languageNames[l]}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <div className="flex items-center gap-1" data-testid="language-selector-mobile">
              {Object.keys(languageNames).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`text-xs font-bold px-1.5 py-0.5 rounded transition-colors ${
                    lang === l
                      ? "bg-primary text-white"
                      : scrolled ? "text-secondary" : "text-white/70"
                  }`}
                >
                  {languageNames[l]}
                </button>
              ))}
            </div>
            <button
              className="p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-button"
            >
            {isOpen ? (
              <X className={scrolled ? "text-secondary" : "text-white"} />
            ) : (
              <Menu className={scrolled ? "text-secondary" : "text-white"} />
            )}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white border-t"
        >
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="block py-2 text-secondary hover:text-primary font-medium uppercase tracking-wider text-sm"
              >
                {link.label}
              </a>
            ))}
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();
  return (
    <section
      id="hero"
      className="hero-section relative min-h-screen flex items-center"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1526676317768-d9b14f15615a?crop=entropy&cs=srgb&fm=jpg&q=85')`,
      }}
      data-testid="hero-section"
    >
      <div className="hero-overlay"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-block px-4 py-1 bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest mb-6">
            {t.hero.date}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
            {t.hero.title1}{" "}
            <span className="gold-gradient-text">{t.hero.title2}</span> {t.hero.title3}{" "}
            <span className="gold-gradient-text">{t.hero.title4}</span>
          </h1>
          <div className="text-base md:text-lg text-gray-300 mb-10 max-w-6xl leading-relaxed space-y-4 text-left columns-1 md:columns-2 gap-8">
            <p dangerouslySetInnerHTML={{ __html: t.hero.para1 }} />
            <p>{t.hero.para2}</p>
            <p>{t.hero.para3}</p>
            <p className="text-primary font-medium">{t.hero.para4}</p>
          </div>

          <div className="flex flex-wrap items-center gap-8 mb-10">
            <div>
              <p className="text-gray-400 text-sm uppercase tracking-wider mb-4">
                {t.hero.countdown}
              </p>
              <CountdownTimer targetDate="2026-04-17T09:00:00+01:00" />
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_7da1e8fe-490e-4a3e-9f5f-ce553b84d2ec/artifacts/msg5ulso_CIAFD%202026.png"
              alt="30 Anos Ciências do Desporto IPBeja"
              className="w-40 md:w-52 h-auto drop-shadow-2xl"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <a href="#registration">
              <Button
                className="btn-gold bg-primary text-primary-foreground hover:bg-primary-hover font-bold uppercase tracking-widest px-8 py-6 rounded-sm text-sm"
                data-testid="hero-register-btn"
              >
                {t.hero.registerBtn}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <a href="#program">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-secondary font-bold uppercase tracking-widest px-8 py-6 rounded-sm text-sm"
                data-testid="hero-program-btn"
              >
                {t.hero.programBtn}
                </Button>
              </a>
            </div>
          </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();
  return (
    <section id="about" className="py-20 md:py-24 bg-white grain-texture relative" data-testid="about-section">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary font-bold uppercase tracking-widest text-sm">
              {t.about.subtitle}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-secondary mt-4 mb-6">
              {t.about.title}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              {t.about.description}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="bg-secondary p-8 text-white">
              <Award className="h-8 w-8 text-primary mb-4" />
              <div className="font-heading text-4xl font-black text-primary mb-2">50+</div>
              <p className="text-gray-400 text-sm uppercase tracking-wider">{t.about.communications}</p>
            </div>
            <div className="bg-gray-100 p-8">
              <Users className="h-8 w-8 text-primary mb-4" />
              <div className="font-heading text-4xl font-black text-secondary mb-2">200+</div>
              <p className="text-gray-500 text-sm uppercase tracking-wider">{t.about.participants}</p>
            </div>
            <div className="bg-gray-100 p-8">
              <Mic className="h-8 w-8 text-primary mb-4" />
              <div className="font-heading text-4xl font-black text-secondary mb-2">10+</div>
              <p className="text-gray-500 text-sm uppercase tracking-wider">{t.about.speakers}</p>
            </div>
            <div className="bg-primary p-8">
              <GraduationCap className="h-8 w-8 text-secondary mb-4" />
              <div className="font-heading text-4xl font-black text-secondary mb-2">2</div>
              <p className="text-secondary/70 text-sm uppercase tracking-wider">{t.about.days}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Committees Section
const CommitteesSection = () => {
  const { t } = useLanguage();
  const [organizingCommittee, setOrganizingCommittee] = useState([]);
  const [scientificCommittee, setScientificCommittee] = useState([]);

  useEffect(() => {
    const fetchCommittees = async () => {
      try {
        const [orgRes, sciRes] = await Promise.all([
          axios.get(`${API}/committees/organizing`),
          axios.get(`${API}/committees/scientific`),
        ]);
        setOrganizingCommittee(orgRes.data.committee);
        setScientificCommittee(sciRes.data.committee);
      } catch (error) {
        console.error("Error fetching committees:", error);
      }
    };
    fetchCommittees();
  }, []);

  return (
    <section id="committees" className="py-20 md:py-24 bg-gray-50" data-testid="committees-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-primary font-bold uppercase tracking-widest text-sm">
            {t.committees.subtitle}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-secondary mt-4">
            {t.committees.title}
          </h2>
        </motion.div>

        <Accordion type="single" collapsible className="w-full space-y-4">
          <AccordionItem value="organizing" className="border border-gray-200 bg-white">
            <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <span className="font-heading font-bold text-secondary text-lg">
                  {t.committees.organizing}
                </span>
                <Badge variant="outline" className="ml-2">{organizingCommittee.length} {t.committees.members}</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-4">
                {organizingCommittee.map((member, index) => (
                  <div
                    key={index}
                    className={`bg-gray-50 border border-gray-100 px-3 py-2 ${member.role ? "border-l-2 border-l-primary" : ""}`}
                  >
                    <h3 className="font-heading font-bold text-secondary text-xs leading-tight">
                      {member.name}
                    </h3>
                    {member.role && (
                      <p className="text-primary text-[10px] font-semibold mt-0.5">{member.role}</p>
                    )}
                    <p className="text-gray-500 text-[10px] leading-tight mt-0.5">{member.institution}</p>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="scientific" className="border border-gray-200 bg-white">
            <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-secondary/10 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-secondary" />
                </div>
                <span className="font-heading font-bold text-secondary text-lg">
                  {t.committees.scientific}
                </span>
                {scientificCommittee.length > 0 ? (
                  <Badge variant="outline" className="ml-2">{scientificCommittee.length} {t.committees.members}</Badge>
                ) : (
                  <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/30">{t.committees.comingSoon}</Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              {scientificCommittee.length > 0 ? (
                <div className="max-h-[420px] overflow-y-auto pt-4 pr-2 custom-scrollbar">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {scientificCommittee.map((member, index) => (
                      <div
                        key={index}
                        className={`bg-gray-50 border border-gray-100 px-3 py-2 ${member.role ? "border-l-2 border-l-primary" : ""}`}
                      >
                        <h3 className="font-heading font-bold text-secondary text-xs leading-tight">
                          {member.name}
                        </h3>
                        {member.role && (
                          <p className="text-primary text-[10px] font-semibold mt-0.5">{member.role}</p>
                        )}
                        <p className="text-gray-500 text-[10px] leading-tight mt-0.5">{member.institution}</p>
                        {member.country && (
                          <p className="text-gray-500 text-[10px] font-medium mt-0.5">{member.country}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">{t.committees.comingSoonText}</p>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  );
};

// Program Section
const ProgramSection = () => {
  const [day1, setDay1] = useState(null);
  const [day2, setDay2] = useState(null);
  const [workshopDetails, setWorkshopDetails] = useState({});
  const [panelDetails, setPanelDetails] = useState({});
  const [bookDetails, setBookDetails] = useState({});
  const [phdDetails, setPhdDetails] = useState({});
  const [networkingDetails, setNetworkingDetails] = useState({});
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedPhd, setSelectedPhd] = useState(null);
  const [selectedNetworking, setSelectedNetworking] = useState(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [showLunchModal, setShowLunchModal] = useState(false);

  const { lang, t } = useLanguage();

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const [d1Res, d2Res, wsRes, panelRes, bookRes, phdRes, netRes] = await Promise.all([
          axios.get(`${API}/program/day1?lang=${lang}`),
          axios.get(`${API}/program/day2?lang=${lang}`),
          axios.get(`${API}/workshops?lang=${lang}`),
          axios.get(`${API}/panels?lang=${lang}`),
          axios.get(`${API}/books?lang=${lang}`),
          axios.get(`${API}/phd-details`),
          axios.get(`${API}/networking-details`),
        ]);
        setDay1(d1Res.data);
        setDay2(d2Res.data);
        setWorkshopDetails(wsRes.data.workshops || {});
        setPanelDetails(panelRes.data.panels || {});
        setBookDetails(bookRes.data.books || {});
        setPhdDetails(phdRes.data.phd || {});
        setNetworkingDetails(netRes.data.networking || {});
      } catch (error) {
        console.error("Error fetching program:", error);
      }
    };
    fetchProgram();
  }, [lang]);

  const getWorkshopId = (item) => {
    // Extract WSP X from strings like "WSP 7 – Moove&Groove..."
    const match = item.match(/^(WSP \d+)/i);
    return match ? match[1].toUpperCase() : null;
  };

  const openWorkshopModal = (workshopId) => {
    const details = workshopDetails[workshopId];
    if (details) {
      setSelectedWorkshop({ id: workshopId, ...details });
    }
  };

  const openPanelModal = (panelId) => {
    const details = panelDetails[panelId];
    if (details) {
      setSelectedPanel({ id: panelId, ...details });
    }
  };

  const openBookModal = (bookId) => {
    const details = bookDetails[bookId];
    if (details) {
      setSelectedBook({ id: bookId, ...details });
    }
  };

  const openPhdModal = (phdId) => {
    const details = phdDetails[phdId];
    if (details) {
      setSelectedPhd({ id: phdId, ...details });
    }
  };

  const WorkshopItem = ({ item }) => {
    const workshopId = getWorkshopId(item);
    const details = workshopId ? workshopDetails[workshopId] : null;
    const hasInfo = details?.trainer_bio || details?.trainer_photo || details?.trainer || details?.description || (details?.trainers && details.trainers.length > 0);

    return (
      <div className="bg-primary/10 border border-primary/30 rounded overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-secondary text-xs font-medium flex-1">{item}</span>
          {hasInfo && (
            <button
              onClick={() => openWorkshopModal(workshopId)}
              className="text-xs px-2 py-1 rounded transition-colors flex items-center gap-1 ml-2 bg-white text-primary border border-primary hover:bg-primary hover:text-white"
              title="Ver mais informação"
            >
              <Info className="h-3 w-3" />
              <span className="hidden sm:inline">Info</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  const getEventIcon = (type) => {
    const icons = {
      keynote: <Mic className="h-5 w-5" />,
      workshop: <Users className="h-5 w-5" />,
      oral: <FileText className="h-5 w-5" />,
      poster: <ClipboardList className="h-5 w-5" />,
      break: <Coffee className="h-5 w-5" />,
      ceremony: <Award className="h-5 w-5" />,
      celebration: <PartyPopper className="h-5 w-5" />,
      registration: <ClipboardList className="h-5 w-5" />,
    };
    return icons[type] || <Calendar className="h-5 w-5" />;
  };

  return (
    <>
    <section id="program" className="py-20 md:py-24 bg-white" data-testid="program-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-primary font-bold uppercase tracking-widest text-sm">
            {t.program.subtitle}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-secondary mt-4">
            {t.program.title}
          </h2>
        </motion.div>

        <Tabs defaultValue="day1" className="w-full">
          <TabsList className="grid w-full max-w-lg mx-auto grid-cols-2 mb-12 bg-gray-200 p-1 rounded-none">
            <TabsTrigger
              value="day1"
              className="rounded-none data-[state=active]:bg-secondary data-[state=active]:text-white font-bold uppercase tracking-wider text-xs"
            >
              <Calendar className="h-4 w-4 mr-2" />
              {t.program.day1Tab}
            </TabsTrigger>
            <TabsTrigger
              value="day2"
              className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold uppercase tracking-wider text-xs"
            >
              <Calendar className="h-4 w-4 mr-2" />
              {t.program.day2Tab}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="day1">
            {day1 && (
              <div className="max-w-4xl mx-auto">
                <div className="bg-secondary text-white p-6 mb-8">
                  <h3 className="font-heading text-2xl font-bold">{day1.date}</h3>
                </div>
                <div className="timeline-connector space-y-6 pl-12">
                  {day1.events.map((event, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="relative bg-white border border-gray-200 p-6 hover:border-primary/50 transition-colors"
                    >
                      <div className="absolute -left-12 top-6 w-10 h-10 bg-primary flex items-center justify-center text-primary-foreground">
                        {getEventIcon(event.type)}
                      </div>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="bg-secondary text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
                              {event.title}
                            </span>
                            {event.time && (
                              <div className="flex items-center gap-2 text-secondary bg-gray-100 px-3 py-1">
                                <Clock className="h-4 w-4" />
                                <span className="text-sm font-medium">{event.time}</span>
                              </div>
                            )}
                          </div>
                          {event.description && (
                            <p className="text-gray-500 text-sm mt-2">{event.description}</p>
                          )}
                          {event.location && (
                            <p className="text-gray-600 text-sm mt-1 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {event.location}
                            </p>
                          )}
                          {event.entities && event.entities.length > 0 && (
                            <div className="mt-3 space-y-1">
                              <p className="text-secondary text-xs font-bold uppercase tracking-wider">{t.program.confirmedPresences}</p>
                              {event.entities.map((entity, idx) => (
                                <p key={idx} className="text-gray-600 text-sm flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0"></span>
                                  {entity}
                                </p>
                              ))}
                            </div>
                          )}
                          {/* Panel/Round Table Info Button */}
                          {event.panel_id && panelDetails[event.panel_id] && (
                            <button
                              onClick={() => openPanelModal(event.panel_id)}
                              className="mt-3 text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1 bg-white text-primary border border-primary hover:bg-primary hover:text-white"
                              title="Ver mais informação"
                            >
                              <Info className="h-3 w-3" />
                              <span>{t.program.speakersInfo}</span>
                            </button>
                          )}
                          {/* Book Launch Info Button */}
                          {event.book_id && bookDetails[event.book_id] && (
                            <button
                              onClick={() => openBookModal(event.book_id)}
                              className="mt-3 text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1 bg-white text-primary border border-primary hover:bg-primary hover:text-white"
                              title="Ver mais informação"
                              data-testid={`book-info-btn-${event.book_id}`}
                            >
                              <Info className="h-3 w-3" />
                              <span>{t.program.bookInfo}</span>
                            </button>
                          )}
                          {/* PhD Presentation Info Button */}
                          {event.phd_id && phdDetails[event.phd_id] && (
                            <button
                              onClick={() => openPhdModal(event.phd_id)}
                              className="mt-3 text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1 bg-white text-primary border border-primary hover:bg-primary hover:text-white"
                              title="Ver mais informação"
                              data-testid={`phd-info-btn-${event.phd_id}`}
                            >
                              <Info className="h-3 w-3" />
                              <span>Info</span>
                            </button>
                          )}
                          {event.items && event.items.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                              {event.items.map((item, itemIndex) => (
                                <WorkshopItem key={itemIndex} item={item} />
                              ))}
                            </div>
                          )}
                          {event.featured_item && (
                            <div className="mt-3 bg-gradient-to-r from-primary/15 via-primary/10 to-primary/15 border-l-4 border-primary rounded-r overflow-hidden">
                              <div className="flex items-center gap-3 px-4 py-3">
                                <GraduationCap className="h-5 w-5 text-primary flex-shrink-0" />
                                <span className="text-secondary text-xs font-bold tracking-wide">{event.featured_item}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="day2">
            {day2 && (
              <div className="max-w-4xl mx-auto">
                <div className="bg-primary text-primary-foreground p-6 mb-8">
                  <h3 className="font-heading text-2xl font-bold">{day2.date}</h3>
                </div>
                <div className="timeline-connector space-y-6 pl-12">
                  {day2.events.map((event, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="relative bg-white border border-gray-200 p-6 hover:border-primary/50 transition-colors"
                    >
                      <div className="absolute -left-12 top-6 w-10 h-10 bg-primary flex items-center justify-center text-primary-foreground">
                        {getEventIcon(event.type)}
                      </div>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="bg-secondary text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
                              {event.title}
                            </span>
                            {event.time && (
                              <div className="flex items-center gap-2 text-secondary bg-gray-100 px-3 py-1">
                                <Clock className="h-4 w-4" />
                                <span className="text-sm font-medium">{event.time}</span>
                              </div>
                            )}
                          </div>
                          {event.description && (
                            <p className="text-gray-500 text-sm mt-2">{event.description}</p>
                          )}
                          {event.location && (
                            <p className="text-gray-600 text-sm mt-1 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {event.location}
                            </p>
                          )}
                          {/* Panel/Round Table Info Button */}
                          {event.panel_id && panelDetails[event.panel_id] && (
                            <button
                              onClick={() => openPanelModal(event.panel_id)}
                              className="mt-3 text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1 bg-white text-primary border border-primary hover:bg-primary hover:text-white"
                              title="Ver mais informação"
                              data-testid={`panel-info-btn-${event.panel_id}`}
                            >
                              <Info className="h-3 w-3" />
                              <span>{t.program.speakersInfo}</span>
                            </button>
                          )}
                          {/* PhD Presentation Info Button (Day 2) */}
                          {event.phd_id && phdDetails[event.phd_id] && (
                            <button
                              onClick={() => openPhdModal(event.phd_id)}
                              className="mt-3 text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1 bg-white text-primary border border-primary hover:bg-primary hover:text-white"
                              title="Ver mais informação"
                              data-testid={`phd-info-btn-${event.phd_id}`}
                            >
                              <Info className="h-3 w-3" />
                              <span>Info</span>
                            </button>
                          )}
                          {/* Networking Info Button (Day 2) */}
                          {event.networking_id && networkingDetails[event.networking_id] && (
                            <button
                              onClick={() => setSelectedNetworking(networkingDetails[event.networking_id])}
                              className="mt-3 text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1 bg-white text-primary border border-primary hover:bg-primary hover:text-white"
                              title="Ver mais informação"
                              data-testid={`networking-info-btn-${event.networking_id}`}
                            >
                              <Info className="h-3 w-3" />
                              <span>Info</span>
                            </button>
                          )}
                          {/* Celebration Lunch Info Button */}
                          {event.type === "celebration" && (
                            <button
                              onClick={() => setShowLunchModal(true)}
                              className="mt-3 text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1 bg-white text-primary border border-primary hover:bg-primary hover:text-white"
                              data-testid="program-lunch-info-btn"
                            >
                              <Info className="h-3 w-3" />
                              <span>+ Info</span>
                            </button>
                          )}
                          {event.items && event.items.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                              {event.items.map((item, itemIndex) => (
                                <WorkshopItem key={itemIndex} item={item} />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Workshop Info Modal */}
      <Dialog open={!!selectedWorkshop} onOpenChange={() => setSelectedWorkshop(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedWorkshop && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-secondary">
                  {selectedWorkshop.id} – {selectedWorkshop.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Multiple Trainers */}
                {selectedWorkshop.trainers && selectedWorkshop.trainers.length > 0 ? (
                  selectedWorkshop.trainers.map((tr, idx) => (
                    <div key={idx} className={`flex items-start gap-4 ${idx > 0 ? "border-t border-gray-200 pt-6" : ""}`}>
                      {tr.photo ? (
                        <img src={tr.photo} alt={tr.name} className="w-24 h-24 rounded-full object-cover flex-shrink-0 border-2 border-primary" />
                      ) : (
                        <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-10 w-10 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-bold text-secondary text-lg mb-2">{tr.name}</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">{tr.bio}</p>
                      </div>
                    </div>
                  ))
                ) : (
                /* Single Trainer Bio */
                (selectedWorkshop.trainer_bio || selectedWorkshop.trainer_photo || selectedWorkshop.trainer) && (
                  <div className="flex items-start gap-4">
                    {selectedWorkshop.trainer_photo ? (
                      <img 
                        src={selectedWorkshop.trainer_photo} 
                        alt={selectedWorkshop.trainer}
                        className="w-24 h-24 rounded-full object-cover flex-shrink-0 border-2 border-primary"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="h-10 w-10 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-bold text-secondary text-lg mb-2">
                        {selectedWorkshop.trainer}
                      </h4>
                      {selectedWorkshop.trainer_bio && (
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {selectedWorkshop.trainer_bio}
                        </p>
                      )}
                    </div>
                  </div>
                )
                )}
                
                {/* Workshop Description */}
                {selectedWorkshop.description && (
                  <div className={(selectedWorkshop.trainer_bio || (selectedWorkshop.trainers && selectedWorkshop.trainers.length > 0)) ? "border-t border-gray-200 pt-6" : ""}>
                    <h4 className="font-bold text-secondary text-base mb-3">
                      {t.program.workshopContent}
                    </h4>
                    <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                      {selectedWorkshop.description}
                    </div>
                  </div>
                )}
                
                {/* Workshop Photos */}
                {selectedWorkshop.photos && selectedWorkshop.photos.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-bold text-secondary text-base mb-3">
                      {t.program.gallery || "Galeria"}
                    </h4>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {selectedWorkshop.photos.map((photo, idx) => (
                        <img 
                          key={idx}
                          src={photo} 
                          alt={`Workshop ${idx + 1}`}
                          className="w-32 h-32 object-cover rounded"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Panel/Round Table Info Modal */}
      <Dialog open={!!selectedPanel} onOpenChange={() => setSelectedPanel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPanel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-secondary">
                  {selectedPanel.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Panel Description */}
                {selectedPanel.description && (
                  <div>
                    <h4 className="font-bold text-secondary text-base mb-3">
                      Sobre a Mesa Redonda
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {selectedPanel.description}
                    </p>
                  </div>
                )}
                
                {/* Speakers */}
                {selectedPanel.speakers && selectedPanel.speakers.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-bold text-secondary text-base mb-4">
                      {t.about.speakers}
                    </h4>
                    <div className="space-y-4">
                      {selectedPanel.speakers.map((speaker, idx) => (
                        <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded">
                          {speaker.photo ? (
                            <img 
                              src={speaker.photo} 
                              alt={speaker.name}
                              className="w-16 h-16 rounded-full object-cover flex-shrink-0 border-2 border-primary"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="h-8 w-8 text-white" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h5 className="font-bold text-secondary text-base">
                              {speaker.name}
                            </h5>
                            <p className="text-gray-600 text-sm leading-relaxed mt-1">
                              {speaker.role}
                            </p>
                            {speaker.bio && (
                              <p className="text-gray-500 text-sm leading-relaxed mt-3 border-t border-gray-200 pt-3">
                                {speaker.bio}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Book Launch Info Modal */}
      <Dialog open={!!selectedBook} onOpenChange={() => setSelectedBook(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedBook && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-secondary">
                  {t.program.bookLaunch}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Book Cover + Title */}
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  {selectedBook.cover && (
                    <img 
                      src={selectedBook.cover} 
                      alt={selectedBook.title}
                      className="w-40 h-auto rounded shadow-lg flex-shrink-0"
                    />
                  )}
                  <div className="text-center sm:text-left">
                    <h3 className="font-heading text-lg font-bold text-secondary">
                      {selectedBook.title}
                    </h3>
                    {selectedBook.description && (
                      <p className="text-gray-600 text-sm leading-relaxed mt-3">
                        {selectedBook.description}
                      </p>
                    )}
                    {selectedBook.preview_url && (
                      <a
                        href={selectedBook.preview_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => { e.stopPropagation(); window.open(selectedBook.preview_url, '_blank'); e.preventDefault(); }}
                        className="inline-flex items-center gap-2 mt-4 text-xs px-4 py-2 rounded bg-primary text-white hover:bg-primary/90 transition-colors font-bold cursor-pointer"
                      >
                        <BookOpen className="h-4 w-4" />
                        {t.program.previewBook}
                      </a>
                    )}
                  </div>
                </div>

                {/* Editor */}
                {selectedBook.editor && (
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-bold text-secondary text-base mb-4">
                      {t.program.editor}
                    </h4>
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded">
                      {selectedBook.editor.photo ? (
                        <img 
                          src={selectedBook.editor.photo} 
                          alt={selectedBook.editor.name}
                          className="w-20 h-20 rounded-full object-cover flex-shrink-0 border-2 border-primary"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-10 w-10 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h5 className="font-bold text-secondary text-base">
                          {selectedBook.editor.name}
                        </h5>
                        <p className="text-gray-600 text-sm leading-relaxed mt-1">
                          {selectedBook.editor.bio}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* PhD Presentation Modal */}
      <Dialog open={!!selectedPhd} onOpenChange={() => setSelectedPhd(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPhd && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-secondary">
                  {selectedPhd.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {/* Speaker */}
                <div className="flex items-start gap-4">
                  {selectedPhd.speaker_photo ? (
                    <img
                      src={selectedPhd.speaker_photo}
                      alt={selectedPhd.speaker}
                      className="w-24 h-24 rounded-full object-cover flex-shrink-0 border-2 border-primary"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-10 w-10 text-white" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h4 className="font-bold text-secondary text-lg mb-2">
                      {selectedPhd.speaker}
                    </h4>
                    {selectedPhd.speaker_bio && (
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {selectedPhd.speaker_bio}
                      </p>
                    )}
                  </div>
                </div>
                {/* Description */}
                {selectedPhd.description && (
                  <div className="border-t border-gray-200 pt-6">
                    <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                      {selectedPhd.description}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Networking Modal */}
      <Dialog open={!!selectedNetworking} onOpenChange={() => setSelectedNetworking(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedNetworking && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-secondary">
                  {selectedNetworking.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 py-4">
                <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                  {selectedNetworking.description}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Speaker Bio Modal */}
      <Dialog open={!!selectedSpeaker} onOpenChange={() => setSelectedSpeaker(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedSpeaker && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-secondary">
                  {selectedSpeaker.name}
                </DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <div className="flex flex-col items-center gap-4 mb-6">
                  {selectedSpeaker.photo ? (
                    <img 
                      src={selectedSpeaker.photo} 
                      alt={selectedSpeaker.name}
                      className="w-24 h-24 rounded-full object-cover border-2 border-primary"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center">
                      <User className="h-12 w-12 text-white" />
                    </div>
                  )}
                  <p className="text-gray-500 text-sm text-center">{selectedSpeaker.role}</p>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-bold text-secondary text-sm mb-3">{t.program.biography}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{selectedSpeaker.bio}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
    {/* Lunch Details Modal (Program Section) */}
    {showLunchModal && (
      <LunchModal onClose={() => setShowLunchModal(false)} />
    )}
    </>
  );
};

// Abstract Submission Section
const SubmissionsSection = () => {
  const { lang, t } = useLanguage();
  const [guidelines, setGuidelines] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionsClosed = new Date() >= new Date('2026-03-21T00:00:00');
  
  // Form state
  const [submissionType, setSubmissionType] = useState("");
  const [thematicArea, setThematicArea] = useState("");
  const [titlePt, setTitlePt] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [affiliations, setAffiliations] = useState([{ id: 1, name: "" }]);
  const [authors, setAuthors] = useState([{ name: "", affiliationIds: [1] }]);
  const [abstractPt, setAbstractPt] = useState("");
  const [abstractEn, setAbstractEn] = useState("");
  const [keywordsPt, setKeywordsPt] = useState([""]);
  const [keywordsEn, setKeywordsEn] = useState([""]);
  const [references, setReferences] = useState("");
  const [correspondingAuthorName, setCorrespondingAuthorName] = useState("");
  const [correspondingAuthorEmail, setCorrespondingAuthorEmail] = useState("");
  const [presentingAuthorName, setPresentingAuthorName] = useState("");
  const [presentingAuthorEmail, setPresentingAuthorEmail] = useState("");

  const thematicAreasCanonical = [
    "Atividade Física e Saúde",
    "Treino e Performance Desportiva",
    "Educação Física e Sociedade",
    "Desporto de Natureza e Sustentabilidade",
  ];
  const thematicAreasDisplay = [
    t.submissions.thematicArea1,
    t.submissions.thematicArea2,
    t.submissions.thematicArea3,
    t.submissions.thematicArea4,
  ];

  useEffect(() => {
    const fetchGuidelines = async () => {
      try {
        const res = await axios.get(`${API}/submission-guidelines`);
        setGuidelines(res.data);
      } catch (error) {
        console.error("Error fetching guidelines:", error);
      }
    };
    fetchGuidelines();
  }, []);

  // Affiliation management
  const addAffiliation = () => {
    const newId = Math.max(...affiliations.map(a => a.id)) + 1;
    setAffiliations([...affiliations, { id: newId, name: "" }]);
  };

  const removeAffiliation = (id) => {
    if (affiliations.length > 1) {
      setAffiliations(affiliations.filter(a => a.id !== id));
      // Remove this affiliation from all authors
      setAuthors(authors.map(author => ({
        ...author,
        affiliationIds: author.affiliationIds.filter(aid => aid !== id)
      })));
    }
  };

  const updateAffiliation = (id, name) => {
    setAffiliations(affiliations.map(a => a.id === id ? { ...a, name } : a));
  };

  // Author management
  const addAuthor = () => {
    if (authors.length < 15) {
      setAuthors([...authors, { name: "", affiliationIds: [] }]);
    }
  };

  const removeAuthor = (index) => {
    if (authors.length > 1) {
      setAuthors(authors.filter((_, i) => i !== index));
    }
  };

  const updateAuthorName = (index, name) => {
    const newAuthors = [...authors];
    newAuthors[index].name = name;
    setAuthors(newAuthors);
  };

  const toggleAuthorAffiliation = (authorIndex, affiliationId) => {
    const newAuthors = [...authors];
    const currentIds = newAuthors[authorIndex].affiliationIds;
    if (currentIds.includes(affiliationId)) {
      newAuthors[authorIndex].affiliationIds = currentIds.filter(id => id !== affiliationId);
    } else {
      newAuthors[authorIndex].affiliationIds = [...currentIds, affiliationId];
    }
    setAuthors(newAuthors);
  };

  // Keywords management - paired PT/EN
  const addKeywordPair = () => {
    if (keywordsPt.length < 5) {
      setKeywordsPt([...keywordsPt, ""]);
      setKeywordsEn([...keywordsEn, ""]);
    }
  };
  const removeKeywordPair = (index) => {
    if (keywordsPt.length > 1) {
      setKeywordsPt(keywordsPt.filter((_, i) => i !== index));
      setKeywordsEn(keywordsEn.filter((_, i) => i !== index));
    }
  };
  const updateKeywordPt = (index, value) => {
    const newKeywords = [...keywordsPt];
    newKeywords[index] = value;
    setKeywordsPt(newKeywords);
  };
  const updateKeywordEn = (index, value) => {
    const newKeywords = [...keywordsEn];
    newKeywords[index] = value;
    setKeywordsEn(newKeywords);
  };

  const resetForm = () => {
    setShowForm(false);
    setSubmissionType("");
    setThematicArea("");
    setTitlePt("");
    setTitleEn("");
    setAffiliations([{ id: 1, name: "" }]);
    setAuthors([{ name: "", affiliationIds: [1] }]);
    setAbstractPt("");
    setAbstractEn("");
    setKeywordsPt([""]);
    setKeywordsEn([""]);
    setReferences("");
    setCorrespondingAuthorName("");
    setCorrespondingAuthorEmail("");
    setPresentingAuthorName("");
    setPresentingAuthorEmail("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!submissionType) {
      toast.error(t.submissions.validationSelectType);
      return;
    }
    if (!thematicArea) {
      toast.error(t.submissions.validationSelectArea);
      return;
    }

    const validAffiliations = affiliations.filter(a => a.name.trim());
    if (validAffiliations.length === 0) {
      toast.error(t.submissions.validationAddAffiliation);
      return;
    }

    const validAuthors = authors.filter(a => a.name.trim());
    if (validAuthors.length === 0) {
      toast.error(t.submissions.validationAddAuthor);
      return;
    }

    // Validate all authors have at least one affiliation
    const authorsWithoutAffiliation = validAuthors.filter(a => !a.affiliationIds || a.affiliationIds.length === 0);
    if (authorsWithoutAffiliation.length > 0) {
      toast.error(t.submissions.validationAuthorAffiliation);
      return;
    }

    const validKeywordsPt = keywordsPt.filter(k => k.trim());
    const validKeywordsEn = keywordsEn.filter(k => k.trim());
    if (validKeywordsPt.length === 0 || validKeywordsEn.length === 0) {
      toast.error(t.submissions.validationAddKeywords);
      return;
    }

    // Validate paired keywords (each PT must have corresponding EN)
    for (let i = 0; i < Math.max(keywordsPt.length, keywordsEn.length); i++) {
      const hasPt = keywordsPt[i] && keywordsPt[i].trim();
      const hasEn = keywordsEn[i] && keywordsEn[i].trim();
      if (hasPt && !hasEn || !hasPt && hasEn) {
        toast.error(t.submissions.validationKeywordsPaired);
        return;
      }
    }

    if (abstractPt.length > 3000) {
      toast.error(t.submissions.validationAbstractPtMax);
      return;
    }
    if (abstractEn.length > 3000) {
      toast.error(t.submissions.validationAbstractEnMax);
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/abstracts`, {
        submission_type: submissionType,
        thematic_area: thematicArea,
        title_pt: titlePt,
        title_en: titleEn,
        affiliations: validAffiliations,
        authors: validAuthors.map(a => ({ name: a.name, affiliation_ids: a.affiliationIds })),
        abstract_pt: abstractPt,
        abstract_en: abstractEn,
        keywords_pt: validKeywordsPt,
        keywords_en: validKeywordsEn,
        references: references || null,
        corresponding_author_name: correspondingAuthorName,
        corresponding_author_email: correspondingAuthorEmail,
        presenting_author_name: presentingAuthorName,
        presenting_author_email: presentingAuthorEmail,
        lang: lang,
      });

      toast.success(t.submissions.submitSuccess, {
        description: t.submissions.submitSuccessDesc,
      });

      resetForm();
    } catch (error) {
      toast.error(t.submissions.submitError, {
        description: error.response?.data?.detail || t.submissions.tryAgain,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="submissions" className="py-20 md:py-24 bg-gray-50" data-testid="submissions-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-primary font-bold uppercase tracking-widest text-sm">
            {t.submissions.subtitle}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-secondary mt-4">
            {t.submissions.title}
          </h2>
        </motion.div>

        {/* Áreas de Submissão */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white border border-gray-200 p-8 mb-8"
        >
          <h3 className="font-heading font-bold text-secondary text-xl mb-4 text-center">
            {t.submissions.areas}
          </h3>
          <p className="text-gray-600 text-center mb-6">
            {t.submissions.areasDesc}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {thematicAreasDisplay.map((area, index) => (
              <div key={index} className="bg-secondary/5 border-l-4 border-primary p-4">
                <p className="font-semibold text-secondary text-sm">{area}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Download Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white border border-gray-200 p-8 mb-8"
        >
          <h3 className="font-heading font-bold text-secondary text-xl mb-6 text-center">
            {t.submissions.documentsTitle}
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border border-gray-200 p-6 text-center hover:border-primary/50 transition-colors">
              <div className="w-16 h-16 bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-secondary" />
              </div>
              <h4 className="font-heading font-bold text-secondary mb-4">{t.submissions.normasTitle}</h4>
              <Button
                variant="outline"
                className="w-full border-secondary text-secondary hover:bg-secondary hover:text-white"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/templates/normas_submissao`, { responseType: 'blob' });
                    saveAs(res.data, "Regras_SubmissaoComunicacoes_10CIAFD.pdf");
                  } catch (e) { console.error(e); }
                }}
              >
                <Download className="mr-2 h-4 w-4" />
                {t.submissions.downloadPdf}
              </Button>
            </div>
            <div className="border border-gray-200 p-6 text-center hover:border-primary/50 transition-colors">
              <div className="w-16 h-16 bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Mic className="h-8 w-8 text-primary" />
              </div>
              <h4 className="font-heading font-bold text-secondary mb-4">{t.submissions.templateOral}</h4>
              <Button
                variant="outline"
                className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/templates/template_comunicacao_oral`, { responseType: 'blob' });
                    saveAs(res.data, "Template_Comunicacao_Oral_10CIAFD.pptx");
                  } catch (e) { console.error(e); }
                }}
              >
                <Download className="mr-2 h-4 w-4" />
                {t.submissions.downloadPptx}
              </Button>
            </div>
            <div className="border border-gray-200 p-6 text-center hover:border-primary/50 transition-colors">
              <div className="w-16 h-16 bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="h-8 w-8 text-primary" />
              </div>
              <h4 className="font-heading font-bold text-secondary mb-4">{t.submissions.templatePoster}</h4>
              <Button
                variant="outline"
                className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/templates/template_eposter`, { responseType: 'blob' });
                    saveAs(res.data, "Template_ePoster_10CIAFD.pptx");
                  } catch (e) { console.error(e); }
                }}
              >
                <Download className="mr-2 h-4 w-4" />
                Download PPTX
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Deadlines and Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-secondary p-8 text-white"
        >
          <div className="grid md:grid-cols-4 gap-6 items-center">
            <div className="text-center">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">{t.submissions.submissionDeadline}</p>
              <p className="font-heading text-lg font-bold text-primary">{t.submissions.deadlineDate1}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">{t.submissions.notificationDate}</p>
              <p className="font-heading text-lg font-bold text-white">{t.submissions.deadlineDate2}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">{t.submissions.authorRegistration}</p>
              <p className="font-heading text-lg font-bold text-white">{t.submissions.deadlineDate3}</p>
            </div>
            <div>
              <Button
                onClick={() => setShowForm(true)}
                className="btn-gold bg-primary text-primary-foreground hover:bg-primary-hover font-bold uppercase tracking-widest px-6 py-6 rounded-sm w-full"
                data-testid="submit-abstract-btn"
                disabled={submissionsClosed}
              >
                <Send className="mr-2 h-4 w-4" />
                {submissionsClosed ? t.submissions.closedBtn : t.submissions.submitBtnAction}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Submission Form Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">{t.submissions.formDialogTitle}</DialogTitle>
              <DialogDescription>
                {t.submissions.formDialogDesc}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Section 1: Type and Thematic Area */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h4 className="font-heading font-bold text-secondary">{t.submissions.section1}</h4>
                
                {/* Submission Type */}
                <div>
                  <Label className="text-secondary font-medium">{t.submissions.presentationType}</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div
                      className={`p-4 border-2 cursor-pointer transition-colors text-center ${
                        submissionType === "oral" ? "border-primary bg-primary/5" : "border-gray-200 hover:border-primary/50"
                      }`}
                      onClick={() => setSubmissionType("oral")}
                    >
                      <Mic className="h-8 w-8 mx-auto mb-2 text-primary" />
                      <span className="font-semibold text-secondary">{t.submissions.oral}</span>
                    </div>
                    <div
                      className={`p-4 border-2 cursor-pointer transition-colors text-center ${
                        submissionType === "poster" ? "border-primary bg-primary/5" : "border-gray-200 hover:border-primary/50"
                      }`}
                      onClick={() => setSubmissionType("poster")}
                    >
                      <ClipboardList className="h-8 w-8 mx-auto mb-2 text-primary" />
                      <span className="font-semibold text-secondary">{t.submissions.poster}</span>
                    </div>
                  </div>
                </div>

                {/* Thematic Area */}
                <div>
                  <Label className="text-secondary font-medium">{t.submissions.thematicAreaLabel}</Label>
                  <Select value={thematicArea} onValueChange={setThematicArea}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={t.submissions.selectArea} />
                    </SelectTrigger>
                    <SelectContent>
                      {thematicAreasCanonical.map((area, i) => (
                        <SelectItem key={i} value={area}>{thematicAreasDisplay[i]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Section 2: Titles */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h4 className="font-heading font-bold text-secondary">{t.submissions.section2}</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="titlePt" className="text-secondary font-medium">{t.submissions.titlePt}</Label>
                    <Input
                      id="titlePt"
                      value={titlePt}
                      onChange={(e) => setTitlePt(e.target.value)}
                      required
                      className="mt-1"
                      placeholder={t.submissions.titlePtPlaceholder}
                    />
                  </div>
                  <div>
                    <Label htmlFor="titleEn" className="text-secondary font-medium">{t.submissions.titleEn}</Label>
                    <Input
                      id="titleEn"
                      value={titleEn}
                      onChange={(e) => setTitleEn(e.target.value)}
                      required
                      className="mt-1"
                      placeholder={t.submissions.titleEnPlaceholder}
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Affiliations */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-heading font-bold text-secondary">{t.submissions.section3}</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addAffiliation}>
                    <Plus className="h-4 w-4 mr-1" />
                    {t.submissions.addAffiliation}
                  </Button>
                </div>
                <div className="bg-primary/5 border-l-4 border-primary p-3 text-sm">
                  <p className="text-gray-600 mb-2">
                    {t.submissions.affiliationFormat}
                  </p>
                  <p className="text-gray-500 italic">
                    {t.submissions.affiliationExample}
                  </p>
                </div>
                <p className="text-sm text-gray-500">
                  {t.submissions.affiliationNote}
                </p>
                <div className="space-y-2">
                  {affiliations.map((affiliation, index) => (
                    <div key={affiliation.id} className="flex gap-2 items-center">
                      <span className="bg-primary text-primary-foreground text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full">
                        {index + 1}
                      </span>
                      <Input
                        placeholder={t.submissions.affiliationPlaceholder}
                        value={affiliation.name}
                        onChange={(e) => updateAffiliation(affiliation.id, e.target.value)}
                        className="flex-1"
                      />
                      {affiliations.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeAffiliation(affiliation.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 4: Authors */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-heading font-bold text-secondary">{t.submissions.section4Title}</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addAuthor}>
                    <Plus className="h-4 w-4 mr-1" />
                    {t.submissions.addAuthor}
                  </Button>
                </div>
                <p className="text-sm text-gray-500">{t.submissions.authorSelectNote}</p>
                <p className="text-sm text-red-500 font-medium">{t.submissions.affiliationWarning}</p>
                <div className="space-y-4">
                  {authors.map((author, authorIndex) => (
                    <div key={authorIndex} className="border border-gray-200 p-3 bg-white rounded">
                      <div className="flex gap-2 items-start mb-2">
                        <span className="text-xs text-gray-500 mt-2">{t.submissions.authorLabel} {authorIndex + 1}:</span>
                        <Input
                          placeholder={t.submissions.authorPlaceholder}
                          value={author.name}
                          onChange={(e) => updateAuthorName(authorIndex, e.target.value)}
                          className="flex-1"
                        />
                        {authors.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAuthor(authorIndex)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 ml-14">
                        {affiliations.filter(a => a.name.trim()).map((affiliation, affIndex) => (
                          <label
                            key={affiliation.id}
                            className={`flex items-center gap-1 px-2 py-1 border rounded cursor-pointer text-xs ${
                              author.affiliationIds.includes(affiliation.id)
                                ? "bg-primary/10 border-primary text-primary"
                                : "bg-gray-50 border-gray-200 text-gray-600"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={author.affiliationIds.includes(affiliation.id)}
                              onChange={() => toggleAuthorAffiliation(authorIndex, affiliation.id)}
                              className="sr-only"
                            />
                            <span className="font-bold">{affIndex + 1}</span>
                            <span className="max-w-32 truncate">{affiliation.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 5: Abstracts */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h4 className="font-heading font-bold text-secondary">{t.submissions.section5}</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="abstractPt" className="text-secondary font-medium">{t.submissions.abstractPt}</Label>
                      <span className={`text-sm ${abstractPt.length > 3000 ? "text-red-500 font-bold" : "text-gray-500"}`}>
                        {abstractPt.length}/3000
                      </span>
                    </div>
                    <Textarea
                      id="abstractPt"
                      value={abstractPt}
                      onChange={(e) => setAbstractPt(e.target.value)}
                      required
                      rows={6}
                      placeholder={t.submissions.abstractPtPlaceholder}
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="abstractEn" className="text-secondary font-medium">{t.submissions.abstractEn}</Label>
                      <span className={`text-sm ${abstractEn.length > 3000 ? "text-red-500 font-bold" : "text-gray-500"}`}>
                        {abstractEn.length}/3000
                      </span>
                    </div>
                    <Textarea
                      id="abstractEn"
                      value={abstractEn}
                      onChange={(e) => setAbstractEn(e.target.value)}
                      required
                      rows={6}
                      placeholder={t.submissions.abstractEnPlaceholder}
                    />
                  </div>
                </div>
              </div>

              {/* Section 6: Keywords - Paired PT/EN */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-heading font-bold text-secondary">{t.submissions.section6}</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addKeywordPair} disabled={keywordsPt.length >= 5}>
                    <Plus className="h-3 w-3 mr-1" />
                    {t.submissions.keywordPlaceholder}
                  </Button>
                </div>
                <div className="space-y-3">
                  {keywordsPt.map((kwPt, i) => (
                    <div key={i} className="bg-white border border-gray-200 p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold text-gray-500">{t.submissions.keywordPlaceholder} {i + 1}</span>
                        {keywordsPt.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeKeywordPair(i)} className="text-red-500 h-6 w-6">
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-gray-400 uppercase">{t.submissions.keywordsPtLabel}</Label>
                          <Input
                            placeholder={`${t.submissions.keywordPlaceholder} ${i + 1}`}
                            value={kwPt}
                            onChange={(e) => updateKeywordPt(i, e.target.value)}
                            className="mt-0.5"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-gray-400 uppercase">{t.submissions.keywordsEnLabel}</Label>
                          <Input
                            placeholder={`${t.submissions.keywordPlaceholderEn} ${i + 1}`}
                            value={keywordsEn[i] || ""}
                            onChange={(e) => updateKeywordEn(i, e.target.value)}
                            className="mt-0.5"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 7: References (Optional) */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h4 className="font-heading font-bold text-secondary">{t.submissions.section7}</h4>
                <Textarea
                  value={references}
                  onChange={(e) => setReferences(e.target.value)}
                  rows={4}
                  placeholder={t.submissions.referencesPlaceholder}
                />
              </div>

              {/* Section 8: Corresponding Author */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h4 className="font-heading font-bold text-secondary">{t.submissions.section8}</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="corrName" className="text-secondary font-medium">{t.submissions.nameLabel}</Label>
                    <Input
                      id="corrName"
                      value={correspondingAuthorName}
                      onChange={(e) => setCorrespondingAuthorName(e.target.value)}
                      required
                      className="mt-1"
                      placeholder={t.submissions.namePlaceholder}
                    />
                  </div>
                  <div>
                    <Label htmlFor="corrEmail" className="text-secondary font-medium">{t.submissions.emailLabel}</Label>
                    <Input
                      id="corrEmail"
                      type="email"
                      value={correspondingAuthorEmail}
                      onChange={(e) => setCorrespondingAuthorEmail(e.target.value)}
                      required
                      className="mt-1"
                      placeholder={t.submissions.emailPlaceholder}
                    />
                  </div>
                </div>
              </div>

              {/* Section 9: Presenting Author */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h4 className="font-heading font-bold text-secondary">{t.submissions.section9}</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="presName" className="text-secondary font-medium">{t.submissions.nameLabel}</Label>
                    <Input
                      id="presName"
                      value={presentingAuthorName}
                      onChange={(e) => setPresentingAuthorName(e.target.value)}
                      required
                      className="mt-1"
                      placeholder={t.submissions.namePlaceholder}
                    />
                  </div>
                  <div>
                    <Label htmlFor="presEmail" className="text-secondary font-medium">{t.submissions.emailLabel}</Label>
                    <Input
                      id="presEmail"
                      type="email"
                      value={presentingAuthorEmail}
                      onChange={(e) => setPresentingAuthorEmail(e.target.value)}
                      required
                      className="mt-1"
                      placeholder={t.submissions.emailPlaceholder}
                    />
                  </div>
                </div>
              </div>

              {/* Email Notice */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 text-blue-700 text-xs" data-testid="submission-email-notice">
                <Mail className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{t.submissions.emailNotice}</span>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-primary-foreground hover:bg-primary-hover font-bold uppercase tracking-widest py-6"
              >
                {isSubmitting ? t.submissions.submitting : t.submissions.submitBtn}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

// Shared Lunch Modal Component
const LunchModal = ({ onClose }) => {
  const { t } = useLanguage();
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white max-w-lg w-full max-h-[85vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="lunch-modal"
      >
        <button onClick={onClose} className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors" data-testid="lunch-modal-close">
          <X className="h-4 w-4" />
        </button>
        <div className="bg-secondary text-white p-6">
          <h3 className="font-heading font-bold text-xl">{t.lunchModal.title}</h3>
          <div className="flex items-start gap-2 mt-2 text-sm text-gray-300">
            <MapPin className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-semibold">{t.lunchModal.location}</p>
              <p>{t.lunchModal.locationDetail}</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <h4 className="font-heading font-bold text-secondary text-base border-b border-gray-200 pb-2">{t.lunchModal.lunchTitle}</h4>
          <div className="bg-primary/10 border border-primary/20 p-3 text-center">
            <p className="font-heading font-bold text-secondary">{t.lunchModal.welcome}</p>
          </div>
          <div>
            <p className="font-semibold text-secondary">{t.lunchModal.couvert}</p>
            <p className="text-gray-600">{t.lunchModal.couvertDesc}</p>
          </div>
          <div>
            <p className="font-semibold text-secondary">{t.lunchModal.soup}</p>
            <p className="text-gray-600">{t.lunchModal.soupDesc}</p>
          </div>
          <div>
            <p className="font-semibold text-secondary">{t.lunchModal.fish}</p>
            <p className="text-gray-600">{t.lunchModal.fishDesc}</p>
          </div>
          <div>
            <p className="font-semibold text-secondary">{t.lunchModal.meat}</p>
            <p className="text-gray-600">{t.lunchModal.meatDesc}</p>
          </div>
          <div>
            <p className="font-semibold text-secondary">{t.lunchModal.desserts}</p>
            <p className="text-gray-600">{t.lunchModal.dessertsDesc}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 p-3">
            <p className="font-semibold text-secondary">{t.lunchModal.drinks}</p>
            <p className="text-gray-600">{t.lunchModal.drinksDesc}</p>
          </div>
          <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20">
            <Music className="h-4 w-4 text-primary flex-shrink-0" />
            <p className="font-semibold text-secondary">{t.lunchModal.liveMusic}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 p-3">
            <p className="font-semibold text-yellow-800">{t.lunchModal.note}</p>
            <p className="text-yellow-700 text-xs">{t.lunchModal.noteDesc}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
const CelebrationSection = () => {
  const { t } = useLanguage();
  const [showLunchModal, setShowLunchModal] = useState(false);
  return (
    <section id="celebration" className="py-20 md:py-24 bg-secondary text-white" data-testid="celebration-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-1 bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest mb-6">
              {t.celebration.date}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-6">
              {t.celebration.title1}{" "}
              <span className="text-primary">{t.celebration.title2}</span> {t.celebration.title3}
            </h2>
            <p className="text-gray-300 leading-relaxed mb-6 text-lg">
              {t.celebration.description1}
            </p>
            <p className="text-gray-300 leading-relaxed mb-8">
              {t.celebration.description2}
            </p>
            <div className="flex flex-wrap items-center gap-6 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/20 flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-white font-bold">{t.celebration.date}</p>
                  <p className="text-gray-400 text-sm">{t.celebration.saturday}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/20 flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-white font-bold">{t.celebration.specialEvent}</p>
                  <p className="text-gray-400 text-sm">{t.celebration.yearsHistory}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 mb-8">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-300 text-sm">{t.celebration.lunchPrice}</p>
                  <p className="text-white font-heading text-3xl font-black">45€</p>
                </div>
                <a href="#registration">
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wider px-6 py-3">
                    {t.celebration.reserveBtn}
                  </Button>
                </a>
                <Button onClick={() => setShowLunchModal(true)} variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold uppercase tracking-wider px-6 py-3" data-testid="celebration-lunch-info-btn">
                  + Info
                </Button>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              {t.celebration.lunchNote}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/20 blur-3xl"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_7da1e8fe-490e-4a3e-9f5f-ce553b84d2ec/artifacts/msg5ulso_CIAFD%202026.png"
                alt="30 Anos Ciências do Desporto IPBeja"
                className="relative w-96 h-auto"
              />
            </div>
          </motion.div>
        </div>
      </div>
      {showLunchModal && (
        <LunchModal onClose={() => setShowLunchModal(false)} />
      )}
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const { t } = useLanguage();
  const [showLunchModal, setShowLunchModal] = useState(false);
  const includedFeatures = [
    t.pricing.feature1,
    t.pricing.feature2,
    t.pricing.feature3,
    t.pricing.feature4,
    t.pricing.feature5,
  ];

  const pricingPeriods = [
    {
      period: t.pricing.period1,
      deadline: t.pricing.period1Deadline,
      recommended: true,
      categories: [
        { name: t.pricing.cat1, price: 10 },
        { name: t.pricing.cat2, price: 20 },
        { name: t.pricing.cat3, price: 35 }
      ]
    },
    {
      period: t.pricing.period2,
      deadline: t.pricing.period2Deadline,
      recommended: false,
      categories: [
        { name: t.pricing.cat1, price: 15 },
        { name: t.pricing.cat2, price: 25 },
        { name: t.pricing.cat3, price: 40 }
      ]
    }
  ];

  return (
    <section id="pricing" className="py-20 md:py-24 bg-gray-50" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-secondary">
            {t.pricing.title}
          </h2>
        </motion.div>

        {/* Included Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white border border-gray-200 p-6 mb-8"
        >
          <h3 className="font-heading font-bold text-secondary text-lg mb-4 text-center">{t.pricing.includesTitle}</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {includedFeatures.map((feature, i) => (
              <span key={i} className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
                {feature}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Pricing Tables */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {pricingPeriods.map((period, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-white border-2 p-6 ${
                period.recommended ? "border-primary" : "border-gray-200"
              }`}
            >
              {period.recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider px-4 py-1">
                    {t.pricing.recommended}
                  </span>
                </div>
              )}
              <div className="text-center mb-6 pt-2">
                <h3 className="font-heading font-bold text-secondary text-xl">
                  {period.period}
                </h3>
                <p className="text-primary font-semibold text-sm mt-1">{period.deadline}</p>
              </div>
              <div className="space-y-4">
                {period.categories.map((cat, i) => (
                  <div key={i} className="flex justify-between items-start gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                    <span className="text-sm text-gray-600 flex-1">{cat.name}</span>
                    <span className="font-heading font-bold text-secondary text-xl whitespace-nowrap">
                      {cat.price}€
                    </span>
                  </div>
                ))}
              </div>
              <Button
                asChild
                className={`w-full mt-6 ${
                  period.recommended
                    ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                    : "bg-secondary hover:bg-secondary/90 text-white"
                }`}
              >
                <a href="#registration">{t.pricing.registerBtn}</a>
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Lunch Add-on */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-secondary text-white p-6 border-2 border-primary"
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/20 flex items-center justify-center flex-shrink-0">
                <PartyPopper className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl">{t.pricing.lunchTitle}</h3>
                <p className="text-gray-300 text-sm">{t.pricing.lunchSubtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-gray-400 text-xs uppercase tracking-wider">{t.pricing.priceLabel}</p>
                <p className="font-heading text-3xl font-black text-primary">45€</p>
              </div>
              <Button onClick={() => setShowLunchModal(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wider" data-testid="lunch-info-btn">
                {t.pricing.learnMore}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Lunch Details Modal */}
        {showLunchModal && (
          <LunchModal onClose={() => setShowLunchModal(false)} />
        )}
      </div>
    </section>
  );
};

// Registration Section
const RegistrationSection = () => {
  const { lang, t } = useLanguage();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    institution: "",
    role: "",
    participation_type: "",
    registration_category: "",
    invoice_name: "",
    invoice_address: "",
    invoice_postal_code: "",
    invoice_country: "",
    nif: "",
    cedula_tef: "",
    include_lunch: false,
    selected_workshops: [],
    xps_has_account: "",
    xps_email: "",
  });
  const [paymentProof, setPaymentProof] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workshopCapacity, setWorkshopCapacity] = useState({});

  // Fetch workshop capacity on mount
  useEffect(() => {
    const fetchCapacity = async () => {
      try {
        const response = await axios.get(`${API}/workshops/capacity`);
        setWorkshopCapacity(response.data.workshops || {});
      } catch (error) {
        console.error("Error fetching workshop capacity:", error);
      }
    };
    fetchCapacity();
  }, []);

  // Determine current registration period
  const today = new Date();
  const firstDeadline = new Date("2026-03-23T00:00:00");
  const secondDeadline = new Date("2026-04-13T00:00:00");
  
  const isFirstPeriod = today < firstDeadline;
  const isSecondPeriod = today >= firstDeadline && today < secondDeadline;
  const registrationsClosed = today >= secondDeadline;

  const categories = {
    student_ipbeja: { 
      name: t.pricing.cat1, 
      price1: 10, 
      price2: 15 
    },
    student_other: { 
      name: t.pricing.cat2, 
      price1: 20, 
      price2: 25 
    },
    professional: { 
      name: t.pricing.cat3, 
      price1: 35, 
      price2: 40 
    },
    lunch_only: {
      name: t.registration.lunchOnly,
      price1: 45,
      price2: 45,
      isLunchOnly: true,
    },
  };

  const isLunchOnly = formData.registration_category === "lunch_only";

  const getCurrentPrice = (category) => {
    if (!category) return null;
    const cat = categories[category];
    if (!cat) return null;
    const basePrice = isFirstPeriod ? cat.price1 : cat.price2;
    return basePrice;
  };

  const getTotalPrice = () => {
    const basePrice = getCurrentPrice(formData.registration_category);
    if (!basePrice) return null;
    if (isLunchOnly) return 45;
    return formData.include_lunch ? basePrice + 45 : basePrice;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t.registration.fileTooLarge, {
          description: t.registration.fileTooLargeDesc,
        });
        return;
      }
      setPaymentProof(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.registration_category) {
      toast.error(t.registration.selectCategory);
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("full_name", formData.full_name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("institution", formData.institution);
      formDataToSend.append("role", formData.role);
      formDataToSend.append("participation_type", formData.participation_type);
      formDataToSend.append("registration_modality", formData.registration_category);
      formDataToSend.append("invoice_name", formData.invoice_name);
      formDataToSend.append("invoice_address", formData.invoice_address);
      formDataToSend.append("invoice_postal_code", formData.invoice_postal_code);
      formDataToSend.append("invoice_country", formData.invoice_country);
      formDataToSend.append("nif", formData.nif);
      formDataToSend.append("cedula_tef", formData.cedula_tef || "");
      formDataToSend.append("include_lunch", formData.include_lunch ? "true" : "false");
      formDataToSend.append("selected_workshops", formData.selected_workshops.join(","));
      if (formData.selected_workshops.includes("WSP 2")) {
        formDataToSend.append("xps_has_account", formData.xps_has_account || "");
        formDataToSend.append("xps_email", formData.xps_email || "");
      }
      
      if (paymentProof) {
        formDataToSend.append("payment_proof", paymentProof);
      }
      formDataToSend.append("lang", lang);

      await axios.post(`${API}/registrations`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(t.registration.success, {
        description: t.registration.successDesc,
      });

      setFormData({
        full_name: "",
        email: "",
        institution: "",
        role: "",
        participation_type: "",
        registration_category: "",
        invoice_name: "",
        invoice_address: "",
        invoice_postal_code: "",
        invoice_country: "",
        nif: "",
        cedula_tef: "",
        include_lunch: false,
        selected_workshops: [],
        xps_has_account: "",
        xps_email: "",
      });
      setPaymentProof(null);
    } catch (error) {
      toast.error(t.registration.error, {
        description: error.response?.data?.detail || t.submissions.tryAgain,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCategory = categories[formData.registration_category];
  const currentPrice = getCurrentPrice(formData.registration_category);

  return (
    <section id="registration" className="py-20 md:py-24 bg-white" data-testid="registration-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary font-bold uppercase tracking-widest text-sm">
              {t.registration.subtitle}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-secondary mt-4 mb-6">
              {t.registration.title}
            </h2>

            {/* Accreditation Info */}
            <div className="space-y-1.5 mb-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Award className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                <span>{t.accreditation.ipdjText}</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                <span>{t.accreditation.teacherText}</span>
              </div>
            </div>

            {/* Current Period Info */}
            <div className={`p-4 mb-6 border-l-4 ${isFirstPeriod ? "border-green-500 bg-green-50" : isSecondPeriod ? "border-yellow-500 bg-yellow-50" : "border-red-500 bg-red-50"}`}>
              {isFirstPeriod && (
                <>
                  <h4 className="font-bold text-green-700">{t.registration.period1Title}</h4>
                  <p className="text-green-600 text-sm">{t.registration.period1Desc}</p>
                </>
              )}
              {isSecondPeriod && (
                <>
                  <h4 className="font-bold text-yellow-700">{t.registration.period2Title}</h4>
                  <p className="text-yellow-600 text-sm">{t.registration.period2Desc}</p>
                </>
              )}
              {registrationsClosed && (
                <>
                  <h4 className="font-bold text-red-700">{t.registration.closedTitle}</h4>
                  <p className="text-red-600 text-sm">{t.registration.closedDesc}</p>
                </>
              )}
            </div>

            {/* Payment Info */}
            <div className="bg-secondary/5 border border-secondary/20 p-4 mb-6">
              <h4 className="font-heading font-bold text-secondary text-sm mb-2 flex items-center gap-2">
                <Euro className="h-4 w-4 text-primary" />
                {t.registration.paymentDataTitle}
              </h4>
              <div className="text-sm space-y-2 bg-white p-4 border border-gray-200">
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-gray-500">IBAN:</span>
                  <span className="font-mono font-medium text-secondary">PT50 0018 0003 3144 3898 0201 5</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-gray-500">BIC/SWIFT:</span>
                  <span className="font-medium">TOTAPTPL</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-gray-500">{t.registration.bank}:</span>
                  <span>Banco Santander Totta S.A</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-gray-500">{t.registration.country}:</span>
                  <span>Portugal</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-gray-500">{t.registration.holder}:</span>
                  <span>Instituto Politécnico Beja</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-gray-500">NIF:</span>
                  <span>680038671</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-1">
                  <span className="text-gray-500">{t.registration.address}:</span>
                  <span className="text-xs">Rua Pedro Soares IPB APT 6155, 7800-295 Beja</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gray-50 p-8 border border-gray-200"
          >
            <h3 className="font-heading text-2xl font-bold text-secondary mb-2">
              {t.registration.formTitle}
            </h3>
            <p className="text-sm text-gray-500 mb-6">{t.registration.formStepNotice}</p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="registration-form">
              {/* Category Selection - INSIDE FORM */}
              <div>
                <Label className="text-secondary font-medium">{t.registration.categoryLabel}</Label>
                <div className="mt-2 space-y-2">
                  {Object.entries(categories).map(([key, cat]) => (
                    <label
                      key={key}
                      className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                        formData.registration_category === key
                          ? "border-primary bg-primary/5"
                          : key === "lunch_only" ? "border-primary/30 bg-primary/5 hover:border-primary" : "border-gray-200 hover:border-primary/50"
                      }`}
                      data-testid={key === "lunch_only" ? "lunch-only-option" : `category-${key}`}
                    >
                      <input
                        type="radio"
                        name="registration_category"
                        checked={formData.registration_category === key}
                        onChange={() => {
                          if (key === "lunch_only") {
                            setFormData({ ...formData, registration_category: "lunch_only", include_lunch: true, selected_workshops: [] });
                          } else {
                            setFormData({ ...formData, registration_category: key, include_lunch: false });
                          }
                        }}
                        className="w-4 h-4 border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-bold text-secondary text-sm">{cat.name}</p>
                        {key === "lunch_only" && (
                          <p className="text-gray-500 text-xs mt-0.5">{t.registration.lunchOnlyDesc}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0 flex items-center gap-2">
                        {isFirstPeriod && !cat.isLunchOnly && (
                          <span className="text-xs text-gray-400 line-through">{cat.price2}€</span>
                        )}
                        <span className="font-heading text-lg font-black text-primary">
                          {cat.isLunchOnly ? 45 : (isFirstPeriod ? cat.price1 : cat.price2)}€
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="full_name" className="text-secondary font-medium">{t.registration.fullName}</Label>
                <Input
                  id="full_name"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="mt-1 rounded-none border-gray-300"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-secondary font-medium">{t.registration.email}</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 rounded-none border-gray-300"
                />
              </div>

              {!isLunchOnly && (
              <div>
                <Label htmlFor="institution" className="text-secondary font-medium">{t.registration.institution}</Label>
                <Input
                  id="institution"
                  type="text"
                  required={!isLunchOnly}
                  value={formData.institution}
                  onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                  className="mt-1 rounded-none border-gray-300"
                />
              </div>
              )}

              {!isLunchOnly && (
              <>
              <div>
                <Label className="text-secondary font-medium">{t.registration.role}</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger className="mt-1 rounded-none border-gray-300">
                    <SelectValue placeholder={t.registration.selectRole} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="estudante_ipbeja">{t.registration.roleStudentIpbeja}</SelectItem>
                    <SelectItem value="diplomado_ipbeja">{t.registration.roleDiplomaIpbeja}</SelectItem>
                    <SelectItem value="professor">{t.registration.roleProfessor}</SelectItem>
                    <SelectItem value="investigador">{t.registration.roleResearcher}</SelectItem>
                    <SelectItem value="profissional_exercicio">{t.registration.roleProfExercise}</SelectItem>
                    <SelectItem value="outro">{t.registration.roleOther}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-secondary font-medium">{t.registration.participationType}</Label>
                <Select value={formData.participation_type} onValueChange={(value) => setFormData({ ...formData, participation_type: value })}>
                  <SelectTrigger className="mt-1 rounded-none border-gray-300">
                    <SelectValue placeholder={t.registration.selectParticipation} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="participante">{t.registration.participantOnly}</SelectItem>
                    <SelectItem value="apresenta_comunicacao">{t.registration.presenterComm}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              </>
              )}

              {!isLunchOnly && (
              <>
              <div>
                <Label htmlFor="cedula_tef" className="text-secondary font-medium">{t.registration.tefLicense}</Label>
                <Input
                  id="cedula_tef"
                  type="text"
                  value={formData.cedula_tef}
                  onChange={(e) => setFormData({ ...formData, cedula_tef: e.target.value })}
                  className="mt-1 rounded-none border-gray-300"
                  placeholder={t.registration.optional}
                />
              </div>

              {/* Workshop Selection - MOVED BEFORE BILLING */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="text-sm text-gray-500 mb-2">{t.registration.workshopSelection}</p>
                <p className="text-xs text-gray-400 mb-4">{t.registration.workshopNote}</p>
                
                {/* Time Slot 1: 14:30 - 16:00 */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-secondary mb-2 bg-gray-100 px-2 py-1">{t.registration.timeSlot1}</p>
                  <div className="space-y-2">
                    {["WSP 1", "WSP 2", "WSP 3", "WSP 4"].map((wsId) => {
                      const info = workshopCapacity[wsId];
                      if (!info) return null;
                      const isSelected = formData.selected_workshops.includes(wsId);
                      const slot1Selection = formData.selected_workshops.find(w => ["WSP 1", "WSP 2", "WSP 3", "WSP 4"].includes(w));
                      const isClosed = wsId === "WSP 2" || wsId === "WSP 3" || (info.available !== undefined && info.available <= 0);
                      const isLimited = !isClosed && info.available !== undefined && info.available <= 5;
                      
                      return (
                        <div key={wsId}>
                        <label 
                          className={`flex items-center gap-3 p-3 border rounded transition-colors ${
                            isClosed
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                              : isSelected 
                                ? 'border-primary bg-primary/5 cursor-pointer' 
                                : 'border-gray-200 hover:border-primary/50 cursor-pointer'
                          }`}
                        >
                          <input
                            type="radio"
                            name="workshop_slot1"
                            checked={isSelected}
                            disabled={isClosed}
                            onChange={() => {
                              if (isClosed) return;
                              const otherSlotWorkshops = formData.selected_workshops.filter(w => !["WSP 1", "WSP 2", "WSP 3", "WSP 4"].includes(w));
                              setFormData({ 
                                ...formData, 
                                selected_workshops: [...otherSlotWorkshops, wsId] 
                              });
                            }}
                            className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          />
                          <p className={`font-medium text-sm flex-1 ${isClosed ? 'text-gray-400 line-through' : 'text-secondary'}`}>{wsId} – {info.title}</p>
                          {isClosed && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium whitespace-nowrap">{t.registration.registrationsClosed}</span>
                          )}
                          {isLimited && (
                            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-medium whitespace-nowrap">{t.registration.limitedSpots} ({info.available})</span>
                          )}
                        </label>
                        {wsId === "WSP 2" && isSelected && (
                          <div className="border border-primary/30 bg-primary/5 p-3 ml-7 mt-1 space-y-2" data-testid="xps-fields">
                            <p className="text-xs font-semibold text-secondary">{t.registration.xpsTitle}</p>
                            <div>
                              <p className="text-xs text-secondary font-medium">{t.registration.xpsHasAccount}</p>
                              <div className="flex gap-4 mt-1">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input type="radio" name="xps_has_account" value="sim" checked={formData.xps_has_account === "sim"} onChange={() => setFormData({ ...formData, xps_has_account: "sim" })} className="w-3.5 h-3.5 border-gray-300 text-primary focus:ring-primary" data-testid="xps-yes" />
                                  <span className="text-xs">{t.registration.xpsYes}</span>
                                </label>
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input type="radio" name="xps_has_account" value="nao" checked={formData.xps_has_account === "nao"} onChange={() => setFormData({ ...formData, xps_has_account: "nao" })} className="w-3.5 h-3.5 border-gray-300 text-primary focus:ring-primary" data-testid="xps-no" />
                                  <span className="text-xs">{t.registration.xpsNo}</span>
                                </label>
                              </div>
                            </div>
                            {formData.xps_has_account && (
                            <div>
                              <Label htmlFor="xps_email" className="text-secondary font-medium text-xs">
                                {formData.xps_has_account === "sim" ? t.registration.xpsEmail : t.registration.xpsEmailNew}
                              </Label>
                              <Input id="xps_email" type="email" value={formData.xps_email} onChange={(e) => setFormData({ ...formData, xps_email: e.target.value })} className="mt-1 rounded-none border-gray-300 h-8 text-sm" placeholder={formData.xps_has_account === "sim" ? t.registration.xpsEmailPlaceholder : t.registration.xpsEmailNewPlaceholder} data-testid="xps-email-input" />
                            </div>
                            )}
                          </div>
                        )}
                        </div>
                      );
                    })}
                    <label className="flex items-center gap-3 p-2 cursor-pointer text-gray-500">
                      <input
                        type="radio"
                        name="workshop_slot1"
                        checked={!formData.selected_workshops.some(w => ["WSP 1", "WSP 2", "WSP 3", "WSP 4"].includes(w))}
                        onChange={() => {
                          setFormData({ 
                            ...formData, 
                            selected_workshops: formData.selected_workshops.filter(w => !["WSP 1", "WSP 2", "WSP 3", "WSP 4"].includes(w)) 
                          });
                        }}
                        className="w-4 h-4 border-gray-300 text-gray-400 focus:ring-gray-400"
                      />
                      <span className="text-sm italic">{t.registration.noWorkshopSlot}</span>
                    </label>
                  </div>
                </div>

                {/* Time Slot 2: 16:30 - 18:00 */}
                <div>
                  <p className="text-xs font-semibold text-secondary mb-2 bg-gray-100 px-2 py-1">{t.registration.timeSlot2}</p>
                  <div className="space-y-2">
                    {["WSP 5", "WSP 6", "WSP 7", "WSP 8"].map((wsId) => {
                      const info = workshopCapacity[wsId];
                      if (!info) return null;
                      const isSelected = formData.selected_workshops.includes(wsId);
                      const isClosed = wsId === "WSP 6" || (info.available !== undefined && info.available <= 0);
                      const isLimited = !isClosed && info.available !== undefined && info.available <= 5;
                      
                      return (
                        <label 
                          key={wsId}
                          className={`flex items-center gap-3 p-3 border rounded transition-colors ${
                            isClosed
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                              : isSelected 
                                ? 'border-primary bg-primary/5 cursor-pointer' 
                                : 'border-gray-200 hover:border-primary/50 cursor-pointer'
                          }`}
                        >
                          <input
                            type="radio"
                            name="workshop_slot2"
                            checked={isSelected}
                            disabled={isClosed}
                            onChange={() => {
                              if (isClosed) return;
                              const otherSlotWorkshops = formData.selected_workshops.filter(w => !["WSP 5", "WSP 6", "WSP 7", "WSP 8"].includes(w));
                              setFormData({ 
                                ...formData, 
                                selected_workshops: [...otherSlotWorkshops, wsId] 
                              });
                            }}
                            className="w-4 h-4 border-gray-300 text-primary focus:ring-primary"
                          />
                          <p className={`font-medium text-sm flex-1 ${isClosed ? 'text-gray-400 line-through' : 'text-secondary'}`}>{wsId} – {info.title}</p>
                          {isClosed && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium whitespace-nowrap">{t.registration.registrationsClosed}</span>
                          )}
                          {isLimited && (
                            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-medium whitespace-nowrap">{t.registration.limitedSpots} ({info.available})</span>
                          )}
                        </label>
                      );
                    })}
                    <label className="flex items-center gap-3 p-2 cursor-pointer text-gray-500">
                      <input
                        type="radio"
                        name="workshop_slot2"
                        checked={!formData.selected_workshops.some(w => ["WSP 5", "WSP 6", "WSP 7", "WSP 8"].includes(w))}
                        onChange={() => {
                          setFormData({ 
                            ...formData, 
                            selected_workshops: formData.selected_workshops.filter(w => !["WSP 5", "WSP 6", "WSP 7", "WSP 8"].includes(w)) 
                          });
                        }}
                        className="w-4 h-4 border-gray-300 text-gray-400 focus:ring-gray-400"
                      />
                      <span className="text-sm italic">{t.registration.noWorkshopSlot}</span>
                    </label>
                  </div>
                </div>
              </div>

              </>
              )}

              {/* Billing Data */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="text-sm text-gray-500 mb-4">{t.registration.billing}</p>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="invoice_name" className="text-secondary font-medium">{t.registration.invoiceName}</Label>
                    <Input
                      id="invoice_name"
                      type="text"
                      required
                      value={formData.invoice_name}
                      onChange={(e) => setFormData({ ...formData, invoice_name: e.target.value })}
                      className="mt-1 rounded-none border-gray-300"
                      placeholder={t.registration.invoiceNamePlaceholder}
                    />
                  </div>

                  <div>
                    <Label htmlFor="invoice_address" className="text-secondary font-medium">{t.registration.invoiceAddress}</Label>
                    <Input
                      id="invoice_address"
                      type="text"
                      required
                      value={formData.invoice_address}
                      onChange={(e) => setFormData({ ...formData, invoice_address: e.target.value })}
                      className="mt-1 rounded-none border-gray-300"
                      placeholder={t.registration.invoiceAddressPlaceholder}
                    />
                  </div>

                  <div>
                    <Label htmlFor="invoice_postal_code" className="text-secondary font-medium">{t.registration.invoicePostalCode}</Label>
                    <Input
                      id="invoice_postal_code"
                      type="text"
                      required
                      value={formData.invoice_postal_code}
                      onChange={(e) => setFormData({ ...formData, invoice_postal_code: e.target.value })}
                      className="mt-1 rounded-none border-gray-300"
                      placeholder={t.registration.invoicePostalCodePlaceholder}
                    />
                  </div>

                  <div>
                    <Label htmlFor="invoice_country" className="text-secondary font-medium">{t.registration.invoiceCountry}</Label>
                    <Input
                      id="invoice_country"
                      type="text"
                      required
                      value={formData.invoice_country}
                      onChange={(e) => setFormData({ ...formData, invoice_country: e.target.value })}
                      className="mt-1 rounded-none border-gray-300"
                      placeholder={t.registration.invoiceCountryPlaceholder}
                    />
                  </div>

                  <div>
                    <Label htmlFor="nif" className="text-secondary font-medium">{t.registration.nif}</Label>
                    <Input
                      id="nif"
                      type="text"
                      required
                      value={formData.nif}
                      onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                      className="mt-1 rounded-none border-gray-300"
                      placeholder={t.registration.nifPlaceholder}
                    />
                  </div>
                </div>
              </div>

              {/* Lunch Option - hidden for lunch_only */}
              {!isLunchOnly && (
              <div className="border-2 border-primary bg-primary/5 p-4">
                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.include_lunch}
                    onChange={(e) => setFormData({ ...formData, include_lunch: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-heading font-bold text-secondary">{t.registration.lunchTitle}</p>
                        <p className="text-gray-500 text-sm">{t.registration.lunchSubtitle}</p>
                      </div>
                      <span className="font-heading text-xl font-bold text-primary">+45€</span>
                    </div>
                  </div>
                </label>
              </div>
              )}

              <div>
                <Label className="text-secondary font-medium">{t.registration.paymentProof}</Label>
                <div className="mt-1 border-2 border-dashed border-gray-300 rounded-none p-4 text-center hover:border-primary transition-colors">
                  <input
                    type="file"
                    id="payment_proof"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="payment_proof" className="cursor-pointer flex flex-col items-center gap-2">
                    {paymentProof ? (
                      <>
                        <Check className="h-8 w-8 text-green-600" />
                        <span className="text-green-600 font-medium">{paymentProof.name}</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-400" />
                        <span className="text-gray-500">{t.registration.uploadFile}</span>
                        <span className="text-gray-400 text-xs">{t.registration.fileTypes}</span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Resumo do Valor */}
              {selectedCategory && (
                <div className="bg-primary/10 border border-primary/30 p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-secondary text-sm">{selectedCategory.name}</span>
                    <span className="font-heading text-lg font-bold text-secondary">{isLunchOnly ? 45 : currentPrice}€</span>
                  </div>
                  {!isLunchOnly && formData.include_lunch && (
                    <div className="flex justify-between items-center text-sm border-t border-primary/20 pt-2 mt-2">
                      <span className="text-secondary">{t.registration.lunchAddon}</span>
                      <span className="font-bold text-secondary">45€</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center border-t border-primary/30 pt-2 mt-2">
                    <span className="font-bold text-secondary">{t.registration.totalPrice}</span>
                    <span className="font-heading text-xl font-bold text-primary">{getTotalPrice()}€</span>
                  </div>
                </div>
              )}

              {/* Email Notice */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 text-blue-700 text-xs" data-testid="registration-email-notice">
                <Mail className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{t.registration.emailNotice}</span>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || (!isLunchOnly && (!formData.role || !formData.participation_type)) || !formData.registration_category || !formData.nif || !formData.invoice_name || !formData.invoice_address || !formData.invoice_postal_code || !formData.invoice_country || !paymentProof || registrationsClosed}
                className="btn-gold w-full bg-primary text-primary-foreground hover:bg-primary-hover font-bold uppercase tracking-widest px-8 py-6 rounded-sm"
              >
                {registrationsClosed 
                  ? t.registration.closed
                  : isSubmitting 
                    ? t.registration.processing
                    : `${t.registration.confirmBtn}${getTotalPrice() ? ` - ${getTotalPrice()}€` : ""}`
                }
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Sponsors Section
// Masters Promotion Section
const THESES_DATA = [
  { title: "Padrões de atividade física durante a gravidez", author: "Curva, Mara Lúcia Silva", year: 2025 },
  { title: "Relação entre barreiras percebidas e desempenho funcional em idosos ativos", author: "Ribeiro, Alexandra Sofia Coelho", year: 2025 },
  { title: "Comportamentos de movimento e saúde mental em adultos com diagnóstico de depressão", author: "Bate, Miguel Dinis Clérigo", year: 2025 },
  { title: "Influência da amplitude e potência dos membros inferiores na velocidade de remate no futebol - Relação com lesões antecedentes", author: "Montemor, Mauro Marques Garcia", year: 2025 },
  { title: "Intervenções comunitárias para um envelhecimento saudável. Promoção da atividade física e redução de comportamento sedentário em idosos", author: "Fernandes, Teresa Alexandra Silvestre", year: 2025 },
  { title: "Sarcopénia e quedas: Impacto na qualidade de vida de idosos", author: "Silva, José Jaime Ferreira da", year: 2025 },
  { title: "Influências sociais no desenvolvimento da competência motora em crianças entre os 6 e os 14", author: "Lopes, Janine Filipa Serrano", year: 2025 },
  { title: "Competência motora em crianças pré-escolares: Associação com atividade física, padrões de sono e tempo de exposição a ecrãs", author: "Trindade, Sandra Nunes Rondão", year: 2025 },
  { title: "Plogging – Atividade física, saúde e bem-estar", author: "Nunes, Eurico Jorge Pereira", year: 2025 },
  { title: "Condição física, perceção de divertimento e barreiras percebidas em idosos ativos", author: "Miranda, Daniela Martins", year: 2025 },
  { title: "Efeitos do HIRT na saúde mental e aptidão física", author: "Conceição, Pedro Afonso Pereira", year: 2025 },
  { title: "Intervenções comunitárias e a promoção de atividade física em idosos. Boas práticas no Baixo Alentejo, Portugal", author: "Ferro, Laura Isabel Monge", year: 2024 },
  { title: "Perfil e caracterização dos praticantes de desporto natureza - análise do impacto nos locais de prática", author: "Gonçalves, Stefano Borges", year: 2024 },
  { title: "Fatores associados à dor lombar crónica: Qual o seu impacto no nível de incapacidade funcional nos Sapadores Bombeiros Portugueses", author: "Mourato, Ricardo Manuel Gaspar", year: 2024 },
  { title: "Equilíbrio postural através da plataforma de forças e indicadores funcionais na avaliação do risco de queda em idosos autónomos", author: "Soares, Thony Leandro Arins", year: 2024 },
  { title: "Diagnóstico e prescrição de treino de força nos doentes com diabetes tipo 2: Intervenção e custo – benefício do profissional do exercício para o SNS", author: "Bento, Paula Cristina Carracha", year: 2024 },
  { title: "Efeito de uma intervenção de exercício físico multicomponente na capacidade funcional, força, resiliência e qualidade de vida em idosos frágeis", author: "Santana, Andreia Filipa Nunes", year: 2024 },
  { title: "Association between physical activity intensity and learning approaches in adolescentes", author: "Ramos, Rúben Alexandre Bicas", year: 2024 },
  { title: "Condições músculo-esqueléticas, qualidade de vida, níveis de stress e ansiedade dos trabalhadores. Criação de um programa de exercício físico laboral", author: "Nogueira, Ana Rita Viegas", year: 2024 },
  { title: "Análise do perfil de intensidade dos árbitros de futebol em competição", author: "Martins, Danamão Ompajafo", year: 2024 },
  { title: "Promoção da atividade física e mobilidade ativa em idosos: Resultados de um programa de intervenção", author: "Ratinho, Fortunata Maria Casqueiro", year: 2023 },
  { title: "Caracterização do esforço dos jogadores de futebol amador em competição", author: "Parreira, José Eduardo Cofones", year: 2023 },
  { title: "Estudo dos efeitos do treino combinado (força e cardiorrespiratório) de baixo volume em jovens adultos ativos no concelho de Alcoutim: Impacto na condição física e composição corporal durante 6 semanas de treino", author: "Romão, Rodrigo Miguel Sequeira", year: 2023 },
  { title: "Qualidade do instrutor das aulas de grupo e a satisfação dos clientes em ginásios", author: "Barbosa, João António de Jesus Barreiros Colaço", year: 2023 },
  { title: "Motivos e barreiras à prática de exercício físico em estudantes internacionais", author: "Mané, Sana", year: 2023 },
  { title: "Indicadores de composição corporal e a sua relação com a saúde", author: "Colubali, Mamadu Yero", year: 2023 },
  { title: "Atividade física em contexto domiciliar e a sua relação com a força em idosos, durante o confinamento COVID-19", author: "Relvas, João Miguel Fialho", year: 2023 },
  { title: "Atividade física, composição corporal, satisfação com a vida e sintomas depressivos: Um estudo em estudantes do ensino superior", author: "Pereira, Sara Isabel Godinho", year: 2023 },
  { title: "Competência motora em crianças da Educação Pré-Escolar", author: "Lopes, André Guilherme do Rosário", year: 2023 },
  { title: "Atividade física, qualidade de vida e saúde em idosos: Contributos da Administração Local", author: "Cachola, António Jaime Coelho", year: 2023 },
  { title: "Qualidade de vida relacionada com a saúde, quedas e sarcopenia em idosos cabo-verdianos", author: "Garcia, Jacob Mendes Furtado", year: 2023 },
  { title: "Oportunidades de estimulação motora no ambiente familiar de crianças dos 18 aos 42 meses", author: "Matos, Bernardo Manuel Gaspar Marreiros de", year: 2022 },
  { title: "Papel do exercício físico domiciliário na manutenção da saúde, durante o confinamento", author: "Rosa, Tiago Filipe Quaresma", year: 2022 },
  { title: "Análise eletromiográfica do exercício supino plano realizado com dois tipos de resistência externa diferentes: Comparação da ativação muscular entre o Supino com Haltere e o Supino na Polia", author: "Rosa, Miguel Fialho Ferreira", year: 2022 },
  { title: "Efeitos do treino combinado de baixo volume em jovens adultos ativos: Impacto na condição física e composição corporal durante 6 semanas de treino em jovens adultos ativos", author: "Martins, Ricardo", year: 2022 },
  { title: "A eficácia do HIIT na promoção da redução de comportamentos sedentários", author: "Bento, Tiago Manuel Paulino da Silva", year: 2022 },
  { title: "Atividade física, aptidão física e stress dos soldados-recruta no início da formação militar", author: "Santos, José Eduardo Correia Sequeira dos", year: 2022 },
  { title: "Efeitos de um programa de treino combinado de três métodos de força em jovens adultos", author: "Costa, Vera Sofia Broa", year: 2022 },
  { title: "Nível de atividade física por acelerometria, mobilidade ativa e estilos de vida em idosos", author: "Fonseca, Inês Alves", year: 2022 },
  { title: "Efeitos do treino intervalado de alta intensidade (HIIT) em adolescentes: Impacto na aptidão física e capacidade cognitiva de adolescentes da cidade de Beja, Portugal", author: "Dias, Renata Alexandra Antunes", year: 2022 },
  { title: "A importância da satisfação dos clientes em Health Clubs na área do fitness e relação com a fidelização nos mesmos", author: "Valentim, Miguel Gonçalves", year: 2021 },
  { title: "Perfil de saúde, condição física e quedas em idosos: Idosos integrados em programas de exercício físico municipais do distrito de Beja, Portugal", author: "Gomes, Margarida", year: 2020 },
  { title: "Estudo epidemiológico das lesões desportivas nos atletas de andebol na categoria de seniores femininos de Angola (Luanda)", author: "Candolo, Vanda Maria Félix Pascoal", year: 2019 },
  { title: "Atividade física e qualidade de vida em utentes do serviço de fisioterapia", author: "Reis, Marta", year: 2018 },
  { title: "Satisfação de clientes na área do fitness: Qualidade, preço e interações pessoais em ginásios e health clubs", author: "Barbosa, Helena", year: 2018 },
  { title: "Efeito de um programa de exercício físico nas medidas antropométricas, aptidão aeróbica e muscular. Um estudo em contexto escolar com alunos entre os 10 e os 13 anos", author: "Morais, Arlindo José Clemente", year: 2017 },
  { title: "Caracterização das habilidades motoras fundamentais em crianças do 1.º ciclo do ensino básico público do Baixo Alentejo", author: "Domingues, Ana", year: 2017 },
  { title: "Prescrição e avaliação da prestação de atletas amadores de BTT, segundo dois métodos de treino diferentes", author: "Constantino, Helena Sofia Morais", year: 2017 },
  { title: "Treino intervalado de alta intensidade: Monitorização e efeito entre géneros", author: "Bento, André", year: 2017 },
];

const TESTIMONIALS = [
  { id: 1, key: "t1" },
  { id: 2, key: "t2" },
  { id: 3, key: "t3" },
  { id: 4, key: "t4" },
  { id: 5, key: "t5" },
  { id: 6, key: "t6" },
  { id: 7, key: "t7" },
];

const TestimonialsCarousel = () => {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-testid="testimonials-carousel">
      <div className="relative min-h-[120px] flex items-center justify-center">
        <button
          onClick={() => setCurrent(prev => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
          className="absolute left-0 top-1/2 -translate-y-1/2 text-white/40 hover:text-primary transition-colors z-10"
          data-testid="testimonial-prev"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <div className="px-10 text-center">
          <Quote className="h-6 w-6 text-primary/40 mx-auto mb-3" />
          <p className="text-gray-300 italic leading-relaxed text-sm md:text-base" data-testid="testimonial-quote">
            {t.masters.testimonials[TESTIMONIALS[current].key]}
          </p>
        </div>
        <button
          onClick={() => setCurrent(prev => (prev + 1) % TESTIMONIALS.length)}
          className="absolute right-0 top-1/2 -translate-y-1/2 text-white/40 hover:text-primary transition-colors z-10"
          data-testid="testimonial-next"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>
      <div className="flex justify-center gap-1.5 mt-4">
        {TESTIMONIALS.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`w-2 h-2 rounded-full transition-colors ${idx === current ? "bg-primary" : "bg-white/20"}`}
            data-testid={`testimonial-dot-${idx}`}
          />
        ))}
      </div>
    </div>
  );
};

const STUDY_PLAN = {
  sem1: [
    { name: "Atividade Física e Composição Corporal", ects: 6.0 },
    { name: "Avaliação da Condição Física e Dispêndio Energético", ects: 5.0 },
    { name: "Intervenção Pedagógica em Atividade Física e Desporto", ects: 5.0 },
    { name: "Literacia Física através do Movimento", ects: 5.0 },
    { name: "Metodologias de Investigação Aplicadas à Atividade Física e Saúde", ects: 4.0 },
    { name: "Psicologia da Atividade Física e Saúde", ects: 5.0 },
  ],
  sem2: [
    { name: "Atividade Física e Envelhecimento Saudável", ects: 6.0, option: true },
    { name: "Atividade Física para Populações Especiais", ects: 4.0 },
    { name: "Avaliação e Prescrição Avançada do Exercício Físico", ects: 5.0 },
    { name: "Estratégias de Promoção de Estilos de Vida Ativos", ects: 5.0 },
    { name: "Otimização da Nutrição para a Atividade Física e Saúde", ects: 5.0 },
    { name: "Planificação do Trabalho de Investigação na Atividade Física e Saúde", ects: 4.0 },
    { name: "Projetos de Avaliação e Intervenção Comunitária", ects: 6.0, option: true },
  ],
  year2: [
    { name: "Dissertação", ects: 60.0 },
  ],
};

const MastersPromoSection = () => {
  const { t } = useLanguage();
  const [showTheses, setShowTheses] = useState(false);
  const [showStudyPlan, setShowStudyPlan] = useState(false);
  const [selectedYear, setSelectedYear] = useState("all");

  const years = [...new Set(THESES_DATA.map(th => th.year))].sort((a, b) => b - a);

  const filteredTheses = selectedYear === "all"
    ? THESES_DATA
    : THESES_DATA.filter(th => th.year === Number(selectedYear));

  const thesesByYear = {};
  filteredTheses.forEach(th => {
    if (!thesesByYear[th.year]) thesesByYear[th.year] = [];
    thesesByYear[th.year].push(th);
  });
  const sortedYears = Object.keys(thesesByYear).sort((a, b) => b - a);

  return (
    <>
    <section className="bg-secondary py-16 md:py-20" data-testid="masters-promo-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-block bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest px-4 py-1.5 mb-4">
            {t.masters.badge}
          </span>
          <p className="text-primary font-heading font-bold uppercase tracking-widest text-sm mb-2">
            {t.masters.subtitle}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-black text-white mb-6">
            {t.masters.title}
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => setShowStudyPlan(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wider px-8 py-3"
              data-testid="masters-cta-btn"
            >
              <FileText className="mr-2 h-4 w-4" />
              {t.masters.studyPlan}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowTheses(!showTheses)}
              className="border-white/30 text-white hover:bg-white/10 font-bold uppercase tracking-wider px-8 py-3"
              data-testid="theses-toggle-btn"
            >
              <BookOpen className="mr-2 h-4 w-4" />
              {t.masters.thesesTitle} ({THESES_DATA.length})
              <ChevronRight className={`ml-2 h-4 w-4 transition-transform ${showTheses ? "rotate-90" : ""}`} />
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Testimonials Carousel */}
      <TestimonialsCarousel />

      {/* Theses Section - Collapsible */}
      {showTheses && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12"
        >
          <div className="text-center mb-8">
            <h3 className="font-heading text-2xl md:text-3xl font-black text-white mb-2" data-testid="theses-title">
              {t.masters.thesesTitle}
            </h3>
          </div>

          {/* Year filter tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="theses-year-filter">
            <button
              onClick={() => setSelectedYear("all")}
              className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                selectedYear === "all"
                  ? "bg-primary text-primary-foreground"
                  : "bg-white/10 text-gray-300 hover:bg-white/20"
              }`}
              data-testid="theses-filter-all"
            >
              {t.masters.allYears} ({THESES_DATA.length})
            </button>
            {years.map(yr => {
              const count = THESES_DATA.filter(th => th.year === yr).length;
              return (
                <button
                  key={yr}
                  onClick={() => setSelectedYear(String(yr))}
                  className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    selectedYear === String(yr)
                      ? "bg-primary text-primary-foreground"
                      : "bg-white/10 text-gray-300 hover:bg-white/20"
                  }`}
                  data-testid={`theses-filter-${yr}`}
                >
                  {yr} ({count})
                </button>
              );
            })}
          </div>

          {/* Theses list grouped by year */}
          <div className="space-y-8 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar" data-testid="theses-list">
            {sortedYears.map(yr => (
              <div key={yr}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-primary text-primary-foreground text-sm font-bold px-3 py-1">
                    {yr}
                  </span>
                  <div className="flex-1 h-px bg-white/20"></div>
                  <span className="text-gray-400 text-xs">
                    {thesesByYear[yr].length} {t.masters.thesesCount}
                  </span>
                </div>
                <div className="grid gap-3">
                  {thesesByYear[yr].map((thesis, idx) => (
                    <div
                      key={`${yr}-${idx}`}
                      className="bg-white/5 border border-white/10 p-4 text-left hover:border-primary/40 transition-colors"
                      data-testid={`thesis-card-${yr}-${idx}`}
                    >
                      <h4 className="text-white text-sm font-medium leading-relaxed mb-2">
                        {thesis.title}
                      </h4>
                      <p className="text-primary text-xs font-semibold">
                        {thesis.author}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </section>

    {/* Study Plan Modal */}
    <Dialog open={showStudyPlan} onOpenChange={setShowStudyPlan}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-secondary">
            {t.masters.studyPlanTitle}
          </DialogTitle>
          <DialogDescription>
            {t.masters.title}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {/* 1º Ano | 1º Semestre */}
          <div>
            <div className="bg-primary/10 border-l-4 border-primary px-4 py-2 mb-3">
              <span className="text-primary font-heading font-bold text-sm">{t.masters.sem1}</span>
              <span className="float-right text-primary font-bold text-sm">ECTS</span>
            </div>
            <div className="space-y-2">
              {STUDY_PLAN.sem1.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-1.5 border-b border-gray-100">
                  <span className="text-secondary text-sm">{item.name}</span>
                  <span className="text-secondary font-semibold text-sm ml-4 flex-shrink-0">{item.ects.toFixed(1).replace('.', ',')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 1º Ano | 2º Semestre */}
          <div>
            <div className="bg-primary/10 border-l-4 border-primary px-4 py-2 mb-3">
              <span className="text-primary font-heading font-bold text-sm">{t.masters.sem2}</span>
              <span className="float-right text-primary font-bold text-sm">ECTS</span>
            </div>
            <div className="space-y-2">
              {STUDY_PLAN.sem2.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-1.5 border-b border-gray-100">
                  <span className="text-secondary text-sm">
                    {item.name}{item.option ? <span className="text-gray-400 text-xs ml-1">({t.masters.option})</span> : ""}
                  </span>
                  <span className="text-secondary font-semibold text-sm ml-4 flex-shrink-0">{item.ects.toFixed(1).replace('.', ',')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 2º Ano | Anual */}
          <div>
            <div className="bg-primary/10 border-l-4 border-primary px-4 py-2 mb-3">
              <span className="text-primary font-heading font-bold text-sm">{t.masters.year2}</span>
              <span className="float-right text-primary font-bold text-sm">ECTS</span>
            </div>
            <div className="space-y-2">
              {STUDY_PLAN.year2.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-1.5 border-b border-gray-100">
                  <span className="text-secondary text-sm font-medium">{item.name}</span>
                  <span className="text-secondary font-semibold text-sm ml-4 flex-shrink-0">{item.ects.toFixed(1).replace('.', ',')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

// Sponsors Section
const SponsorsSection = () => {
  const { t } = useLanguage();
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const res = await axios.get(`${API}/sponsors`);
        setSponsors(res.data.sponsors);
      } catch (error) {
        console.error("Error fetching sponsors:", error);
      }
    };
    fetchSponsors();
  }, []);

  return (
    <section id="sponsors" className="py-20 md:py-24 bg-gray-50" data-testid="sponsors-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-secondary mt-4">
            {t.sponsors.title}
          </h2>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-4">
            {sponsors.map((sponsor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 p-3 hover:border-primary/50 transition-colors w-40"
              >
                <div className="text-center">
                  <p className="text-gray-500 text-[10px] mb-2">{sponsor.type}</p>
                  {sponsor.logo && (
                    <div className="mb-2 overflow-hidden bg-black mx-auto aspect-square flex items-center justify-center" style={{ maxWidth: '110px' }}>
                      <img
                        src={sponsor.logo}
                        alt={sponsor.name}
                        className="w-full h-auto"
                        style={{ display: 'block' }}
                      />
                    </div>
                  )}
                  <h3 className="font-heading font-bold text-secondary text-xs">
                    {sponsor.name}
                  </h3>
                </div>
              </motion.div>
            ))}
            {/* Visit Portugal - Financiador */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: sponsors.length * 0.1 }}
              className="bg-white border border-gray-200 p-3 hover:border-primary/50 transition-colors w-40"
              data-testid="supporter-section"
            >
              <div className="text-center">
                <p className="text-gray-500 text-[10px] mb-2">{t.sponsors.supportedBy}</p>
                <div className="mb-2 overflow-hidden bg-black mx-auto aspect-square flex items-center justify-center" style={{ maxWidth: '110px' }}>
                  <img
                    src="https://customer-assets.emergentagent.com/job_181dbdb7-85e8-4696-8d97-a67ed2052115/artifacts/n3fzvdr6_visit_Portugal_TwoLines_NEG.png"
                    alt="Visit Portugal"
                    className="w-3/4 h-auto"
                    style={{ display: 'block' }}
                    data-testid="supporter-logo"
                  />
                </div>
              </div>
            </motion.div>
            {/* Visit Alentejo - Apoio */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (sponsors.length + 1) * 0.1 }}
              className="bg-white border border-gray-200 p-3 hover:border-primary/50 transition-colors w-40"
              data-testid="visit-alentejo-section"
            >
              <div className="text-center">
                <p className="text-gray-500 text-[10px] mb-2">{t.sponsors.withSupportOf}</p>
                <div className="mb-2 overflow-hidden bg-black mx-auto aspect-square flex items-center justify-center" style={{ maxWidth: '110px' }}>
                  <img
                    src="/visit_alentejo.png"
                    alt="Visit Alentejo Portugal"
                    className="w-3/4 h-auto"
                    style={{ display: 'block' }}
                    data-testid="visit-alentejo-logo"
                  />
                </div>
              </div>
            </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="bg-secondary text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div>
            <h3 className="font-heading text-2xl font-bold mb-4">
              CIAFD<span className="text-primary">2026</span>
            </h3>
            <p className="text-gray-400 leading-relaxed">
              {t.footer.description}
            </p>
          </div>

          <div>
            <h4 className="font-heading font-bold text-lg mb-4 text-primary">{t.footer.contacts}</h4>
            <div className="space-y-3">
              <a href="mailto:ciafd.ipbeja@gmail.com" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors">
                <Mail className="h-5 w-5" />
                ciafd.ipbeja@gmail.com
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <MapPin className="h-5 w-5 mt-1 flex-shrink-0" />
                <span>Campus do Instituto Politécnico de Beja<br />Rua Pedro Soares<br />7800-295 Beja</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-bold text-lg mb-4 text-primary">{t.footer.links}</h4>
            <div className="space-y-2">
              <a href="#about" className="block text-gray-400 hover:text-white transition-colors">{t.footer.about}</a>
              <a href="#program" className="block text-gray-400 hover:text-white transition-colors">{t.footer.program}</a>
              <a href="#submissions" className="block text-gray-400 hover:text-white transition-colors">{t.footer.submissions}</a>
              <a href="#pricing" className="block text-gray-400 hover:text-white transition-colors">{t.footer.prices}</a>
              <a href="#registration" className="block text-gray-400 hover:text-white transition-colors">{t.footer.registration}</a>
              <a href="/admin" className="block text-gray-400 hover:text-white transition-colors">
                <Shield className="h-4 w-4 inline mr-2" />
                {t.footer.admin}
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-500 text-sm">
            {t.footer.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Admin Panel
const AdminPanel = () => {
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [registrations, setRegistrations] = useState([]);
  const [abstracts, setAbstracts] = useState([]);
  const [presentationUploads, setPresentationUploads] = useState([]);
  const [workshopStats, setWorkshopStats] = useState({});
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);
  const [showProofDialog, setShowProofDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("registrations");
  const [selectedAbstract, setSelectedAbstract] = useState(null);
  const [showAbstractDialog, setShowAbstractDialog] = useState(false);
  const [assignees, setAssignees] = useState({});
  const [reviewers, setReviewers] = useState({});
  const navigate = useNavigate();

  const paymentStatuses = {
    pending: { label: t.admin.pending, color: "bg-yellow-100 text-yellow-800" },
    confirmed: { label: t.admin.confirmedStatus, color: "bg-green-100 text-green-800" },
    confirmed_with_error: { label: "Confirmed w/ error", color: "bg-orange-100 text-orange-800" }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/login`, {}, {
        auth: { username: "admin", password: password }
      });
      setIsAuthenticated(true);
      localStorage.setItem("adminAuth", btoa(`admin:${password}`));
      toast.success(t.admin.loginSuccess);
    } catch (error) {
      toast.error(t.admin.wrongPassword);
    } finally {
      setIsLoading(false);
    }
  };

  const getAuthHeader = () => {
    const auth = localStorage.getItem("adminAuth");
    return auth ? { Authorization: `Basic ${auth}` } : {};
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [regRes, absRes, statsRes, uploadsRes, wsCapRes] = await Promise.all([
        axios.get(`${API}/admin/registrations`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/abstracts`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/presentation-uploads`, { headers: getAuthHeader() }),
        axios.get(`${API}/workshops/capacity`),
      ]);
      setRegistrations(regRes.data.registrations);
      setAbstracts(absRes.data.abstracts);
      setStats(statsRes.data);
      setPresentationUploads(uploadsRes.data.uploads || []);
      setWorkshopStats(wsCapRes.data.workshops || {});
      if (absRes.data.assignees) setAssignees(absRes.data.assignees);
      if (absRes.data.reviewers) setReviewers(absRes.data.reviewers);
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem("adminAuth");
      }
      toast.error(t.admin.errorLoading);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const auth = localStorage.getItem("adminAuth");
    if (auth) setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated]);

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/registrations/${id}/status?status=${status}`, {}, { headers: getAuthHeader() });
      toast.success(t.admin.statusUpdated);
      fetchData();
    } catch (error) {
      toast.error(t.admin.errorUpdating);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/registrations/${id}`, { headers: getAuthHeader() });
      toast.success(t.admin.deleted);
      fetchData();
    } catch (error) {
      toast.error(t.admin.errorDeleting);
    }
  };

  const [rejectionDialog, setRejectionDialog] = useState({ open: false, abstractId: null });
  const [rejectionReasons, setRejectionReasons] = useState("");

  const handleUpdateAbstractStatus = async (id, status, reasons = "") => {
    try {
      await axios.put(`${API}/admin/abstracts/${id}/status`, { status, rejection_reasons: reasons }, { headers: getAuthHeader() });
      toast.success(t.admin.statusUpdated);
      if (status === "accepted") toast.info("Email de aceitação enviado para o autor");
      if (status === "revision") toast.info("Email de revisão enviado para o autor");
      if (status === "rejected") toast.info("Email de rejeição enviado para o autor");
      fetchData();
    } catch (error) {
      toast.error(t.admin.errorUpdating);
    }
  };

  const handleRejectWithReasons = () => {
    handleUpdateAbstractStatus(rejectionDialog.abstractId, "rejected", rejectionReasons);
    setRejectionDialog({ open: false, abstractId: null });
    setRejectionReasons("");
  };

  const handleUpdateAbstractAssignment = async (id, assignedTo, reviewer) => {
    try {
      await axios.put(`${API}/admin/abstracts/${id}/assignment`, {
        assigned_to: assignedTo,
        reviewer: reviewer
      }, { headers: getAuthHeader() });
      toast.success(t.admin.assignmentUpdated);
      if (reviewer) {
        toast.info(t.admin.emailSentToReviewer);
      }
      fetchData();
    } catch (error) {
      toast.error(t.admin.errorUpdating);
    }
  };

  const handleDownloadWord = async (id, seqId) => {
    try {
      const response = await fetch(`${API}/admin/abstracts/${id}/word`, { headers: getAuthHeader() });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${seqId || id}_resumo.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      toast.error(`Erro ao exportar: ${error.message}`);
    }
  };

  const handleExportExcel = async (type) => {
    try {
      let url;
      let filename;
      if (type === "registrations") {
        url = `${API}/admin/export/excel`;
        filename = "inscricoes_ciafd2026.xlsx";
      } else if (type === "abstracts") {
        url = `${API}/admin/export/abstracts/excel`;
        filename = "resumos_ciafd2026.xlsx";
      }
      const response = await fetch(url, { headers: getAuthHeader() });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      toast.error(`Erro ao exportar: ${error.message}`);
    }
  };

  const handleDeleteAbstract = async (id) => {
    try {
      await axios.delete(`${API}/admin/abstracts/${id}`, { headers: getAuthHeader() });
      toast.success(t.admin.deleted);
      fetchData();
    } catch (error) {
      toast.error(t.admin.errorDeleting);
    }
  };

  const handleSendReviewerEmail = async (id, reviewerKey) => {
    if (!reviewerKey) {
      toast.error(t.admin.assign);
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/abstracts/${id}/send-reviewer-email`, {}, { headers: getAuthHeader() });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar email");
    }
  };

  const handleViewProof = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/registrations/${id}/payment-proof`, { headers: getAuthHeader() });
      setSelectedProof(res.data);
      setShowProofDialog(true);
    } catch (error) {
      toast.error("Comprovativo não encontrado");
    }
  };

  const handleDownloadProof = async (id, sequentialId) => {
    try {
      const res = await axios.get(`${API}/admin/registrations/${id}/payment-proof`, { headers: getAuthHeader() });
      if (res.data && res.data.data) {
        // Determine file extension from original filename or default to pdf
        const originalFilename = res.data.filename || "comprovativo.pdf";
        const ext = originalFilename.split('.').pop() || 'pdf';
        const filename = `${sequentialId || 'I0'}_comprovativo.${ext}`;
        
        // Create download link
        const link = document.createElement('a');
        link.href = `data:application/octet-stream;base64,${res.data.data}`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      toast.error("Comprovativo não encontrado");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setIsAuthenticated(false);
    navigate("/");
  };

  // Presentation uploads handlers
  const handleDownloadPresentation = async (uploadId, submissionId, presentationType) => {
    try {
      toast.info("A preparar download...");
      
      const response = await axios.get(`${API}/admin/presentation-uploads/${uploadId}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Get extension from original filename in content-disposition or default
      let ext = 'pptx';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=([^;\n]*)/);
        if (match && match[1]) {
          const originalFilename = match[1].replace(/['"]/g, '').trim();
          const parts = originalFilename.split('.');
          if (parts.length > 1) {
            ext = parts.pop();
          }
        }
      }
      
      const filename = `${submissionId}.${ext}`;
      
      // Use file-saver library
      saveAs(response.data, filename);
      toast.success(`Ficheiro guardado: ${filename}`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Erro ao fazer download do ficheiro");
    }
  };

  const handleDeletePresentation = async (uploadId) => {
    try {
      await axios.delete(`${API}/admin/presentation-uploads/${uploadId}`, { headers: getAuthHeader() });
      toast.success("Ficheiro eliminado");
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar ficheiro");
    }
  };

  const roleLabels = { student: "Estudante", "ex-student": "Ex-Estudante", professor: "Professor", "ex-professor": "Ex-Professor", researcher: "Investigador", professional: "Profissional", other: "Outro" };
  const modalityLabels = { day17: "Dia 17", day18: "Dia 18", both: "Ambos" };
  const typeLabels = { oral: t.submissions.oral, poster: t.submissions.poster };
  const statusLabels = { pending: t.admin.pending, accepted: t.admin.accepted, rejected: t.admin.rejected, revision: t.admin.revision };
  const statusColors = { pending: "bg-yellow-500", accepted: "bg-green-600", rejected: "bg-red-600", revision: "bg-orange-500" };
  const presentationTypeLabels = { oral: t.upload.oralComm, poster: t.upload.poster };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-8 shadow-lg max-w-md w-full">
          <div className="text-center mb-8">
            <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-secondary">{t.admin.title}</h1>
            <p className="text-gray-500 mt-2">CIAFD 2026</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="password">{t.admin.password}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" placeholder={t.admin.enterPassword} />
            </div>
            <Button type="submit" disabled={isLoading} className="w-full bg-primary text-primary-foreground hover:bg-primary-hover">
              {isLoading ? t.admin.loggingIn : t.admin.login}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-gray-500 hover:text-primary">{t.admin.backToSite}</a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-secondary text-white p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Shield className="h-6 w-6 text-primary" />
            <h1 className="font-heading text-xl font-bold">CIAFD<span className="text-primary">2026</span> - Admin</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handleExportExcel("registrations")} className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
              <FileSpreadsheet className="h-4 w-4 mr-2" />{t.admin.registrations}
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExportExcel("abstracts")} className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
              <FileSpreadsheet className="h-4 w-4 mr-2" />{t.admin.abstracts}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="h-4 w-4 mr-2" />{t.admin.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Stats */}
        {stats && (
          <div className="space-y-4 mb-8">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="bg-white p-4 shadow-sm">
                <p className="text-gray-500 text-xs uppercase">{t.admin.registrations}</p>
                <p className="font-heading text-2xl font-bold text-secondary">{stats.total_registrations}</p>
              </div>
              <div className="bg-white p-4 shadow-sm">
                <p className="text-gray-500 text-xs uppercase">{t.admin.confirmed}</p>
                <p className="font-heading text-2xl font-bold text-green-600">{stats.confirmed}</p>
              </div>
              <div className="bg-white p-4 shadow-sm">
                <p className="text-gray-500 text-xs uppercase">{t.admin.revenue}</p>
                <p className="font-heading text-2xl font-bold text-primary">{stats.revenue?.total || 0}</p>
              </div>
              <div className="bg-white p-4 shadow-sm">
                <p className="text-gray-500 text-xs uppercase">{t.admin.abstracts}</p>
                <p className="font-heading text-2xl font-bold text-secondary">{stats.abstracts?.total || 0}</p>
              </div>
              <div className="bg-white p-4 shadow-sm">
                <p className="text-gray-500 text-xs uppercase">{t.admin.accepted}</p>
                <p className="font-heading text-2xl font-bold text-green-600">{stats.abstracts?.accepted || 0}</p>
              </div>
              <div className="bg-white p-4 shadow-sm">
                <p className="text-gray-500 text-xs uppercase flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Visitas Hoje
                </p>
                <p className="font-heading text-2xl font-bold text-blue-600">{stats.pageviews?.today || 0}</p>
                <p className="text-xs text-gray-400">Total: {stats.pageviews?.total || 0}</p>
              </div>
            </div>
            
            {/* Abstracts by thematic area */}
            {stats.abstracts?.by_thematic_area && (
              <div className="bg-white p-4 shadow-sm">
                <p className="text-gray-500 text-xs uppercase mb-3">Resumos por Eixo Temático</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(stats.abstracts.by_thematic_area).map(([area, count]) => (
                    <div key={area} className="border-l-4 border-primary pl-3 py-1">
                      <p className="text-xs text-gray-600 truncate" title={area}>{area}</p>
                      <p className="font-bold text-secondary">
                        {count} <span className="text-green-600 text-sm">({stats.abstracts.accepted_by_thematic_area?.[area] || 0} aceites)</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Page views chart - last 7 days */}
            {stats.pageviews?.last_7_days && (
              <div className="bg-white p-4 shadow-sm">
                <p className="text-gray-500 text-xs uppercase mb-3 flex items-center gap-1">
                  <BarChart3 className="h-3 w-3" /> Visitas (Últimos 7 dias)
                </p>
                <div className="flex items-end gap-2 h-20">
                  {[...stats.pageviews.last_7_days].reverse().map((day, idx) => {
                    const maxViews = Math.max(...stats.pageviews.last_7_days.map(d => d.views), 1);
                    const height = (day.views / maxViews) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-blue-500 rounded-t transition-all"
                          style={{ height: `${Math.max(height, 5)}%` }}
                          title={`${day.date}: ${day.views} visitas`}
                        />
                        <span className="text-xs text-gray-400">{day.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Workshop Registration Stats */}
            {Object.keys(workshopStats).length > 0 && (
              <div className="bg-white p-4 shadow-sm">
                <p className="text-gray-500 text-xs uppercase mb-3">{t.admin.workshopStats}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(workshopStats).map(([wsId, info]) => {
                    const percentage = (info.current / info.max) * 100;
                    const isFull = percentage >= 100;
                    const isNearFull = percentage >= 80;
                    
                    return (
                      <div key={wsId} className="border rounded p-2">
                        <p className="text-xs font-bold text-secondary">{wsId}</p>
                        <p className="text-xs text-gray-500 truncate" title={info.title}>{info.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
                            <div 
                              className={`h-full transition-all ${isFull ? 'bg-red-500' : isNearFull ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs font-bold ${isFull ? 'text-red-600' : isNearFull ? 'text-yellow-600' : 'text-green-600'}`}>
                            {info.current}/{info.max}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-xl grid-cols-3 mb-6">
            <TabsTrigger value="registrations">{t.admin.registrations} ({registrations.length})</TabsTrigger>
            <TabsTrigger value="abstracts">{t.admin.abstracts} ({abstracts.length})</TabsTrigger>
            <TabsTrigger value="presentations">{t.admin.presentations} ({presentationUploads.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="registrations">
            <div className="bg-white shadow-sm overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="font-heading text-lg font-bold text-secondary">{t.admin.registrations}</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExportExcel("registrations")}>
                    <Download className="h-4 w-4 mr-1" />Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchData}>{t.admin.refresh}</Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>{t.admin.name}</TableHead>
                      <TableHead>{t.admin.email}</TableHead>
                      <TableHead>{t.registration.billing}</TableHead>
                      <TableHead>{t.admin.price}</TableHead>
                      <TableHead>{t.admin.proof}</TableHead>
                      <TableHead>{t.admin.status}</TableHead>
                      <TableHead>{t.admin.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registrations.map((reg) => (
                      <TableRow key={reg.id}>
                        <TableCell className="font-bold text-primary">{reg.sequential_id || "-"}</TableCell>
                        <TableCell className="font-medium">{reg.full_name}</TableCell>
                        <TableCell>{reg.email}</TableCell>
                        <TableCell className="text-xs">
                          <div className="font-medium">{reg.invoice_name || reg.full_name}</div>
                          <div className="text-gray-500">{reg.invoice_address || "-"}</div>
                          <div className="text-gray-500">{reg.invoice_postal_code || "-"}</div>
                          <div className="text-gray-500">{reg.invoice_country || "-"}</div>
                          <div className="text-gray-500">NIF: {reg.nif || "-"}</div>
                        </TableCell>
                        <TableCell className="font-bold">{reg.price || 0}€</TableCell>
                        <TableCell>
                          {reg.payment_proof_filename ? (
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadProof(reg.id, reg.sequential_id)} className="text-primary">
                              <Download className="h-4 w-4 mr-1" />
                            </Button>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={reg.payment_status || "pending"}
                            onValueChange={(value) => handleUpdateStatus(reg.id, value)}
                          >
                            <SelectTrigger className={`w-40 text-xs ${paymentStatuses[reg.payment_status]?.color || ""}`}>
                              <SelectValue>{paymentStatuses[reg.payment_status]?.label || t.admin.pending}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">{t.admin.pending}</SelectItem>
                              <SelectItem value="confirmed">{t.admin.confirmedStatus}</SelectItem>
                              <SelectItem value="confirmed_with_error">Confirmed w/ error</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(reg.id)} className="text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {registrations.length === 0 && <div className="p-8 text-center text-gray-500">{t.admin.noRegistrations}</div>}
            </div>
          </TabsContent>

          <TabsContent value="abstracts">
            <div className="bg-white shadow-sm overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="font-heading text-lg font-bold text-secondary">{t.admin.abstracts}</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExportExcel("abstracts")}>
                    <Download className="h-4 w-4 mr-1" />Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchData}>{t.admin.refresh}</Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>{t.admin.type}</TableHead>
                      <TableHead>{t.admin.thematicArea}</TableHead>
                      <TableHead>{t.admin.titleLabel}</TableHead>
                      <TableHead>{t.admin.responsible}</TableHead>
                      <TableHead>{t.admin.reviewer}</TableHead>
                      <TableHead>{t.admin.reviewer}</TableHead>
                      <TableHead>{t.admin.status}</TableHead>
                      <TableHead>{t.admin.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {abstracts.map((abs) => (
                      <TableRow key={abs.id}>
                        <TableCell className="font-bold text-primary">{abs.sequential_id || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={abs.submission_type === "oral" ? "border-blue-500 text-blue-600" : "border-purple-500 text-purple-600"}>
                            {typeLabels[abs.submission_type]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-24 truncate" title={abs.thematic_area}>
                          {abs.thematic_area?.split(" ").slice(0, 2).join(" ")}...
                        </TableCell>
                        <TableCell className="font-medium max-w-xs">
                          <button 
                            onClick={() => { setSelectedAbstract(abs); setShowAbstractDialog(true); }}
                            className="text-left hover:text-primary hover:underline"
                          >
                            {abs.title_pt || abs.title}
                          </button>
                        </TableCell>
                        <TableCell className="text-xs">
                          <div>{abs.corresponding_author_name || "-"}</div>
                          <div className="text-gray-500">{abs.corresponding_author_email}</div>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={abs.assigned_to || "none"}
                            onValueChange={(value) => handleUpdateAbstractAssignment(abs.id, value === "none" ? "" : value, abs.reviewer)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs">
                              <SelectValue placeholder="Atribuir" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nenhum</SelectItem>
                              {Object.entries(assignees).map(([key, val]) => (
                                <SelectItem key={key} value={key}>{val.name.split(" ")[0]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={abs.reviewer || "none"}
                            onValueChange={(value) => handleUpdateAbstractAssignment(abs.id, abs.assigned_to, value === "none" ? "" : value)}
                          >
                            <SelectTrigger className="w-32 h-8 text-xs">
                              <SelectValue placeholder="Revisor" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nenhum</SelectItem>
                              {Object.entries(reviewers).map(([key, val]) => (
                                <SelectItem key={key} value={key}>{val.name.split(" ")[0]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[abs.status] || "bg-gray-500"}>
                            {statusLabels[abs.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadWord(abs.id, abs.sequential_id)} className="text-blue-600" title="Download Word">
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleSendReviewerEmail(abs.id, abs.reviewer)} 
                              className={abs.reviewer ? "text-purple-600 hover:text-purple-800" : "text-gray-300 cursor-not-allowed"}
                              title={abs.reviewer ? `Enviar email ao revisor (${reviewers[abs.reviewer]?.name || ""})` : "Atribua um revisor primeiro"}
                              disabled={!abs.reviewer}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleUpdateAbstractStatus(abs.id, "accepted")} className="text-green-600" title="Aceitar">
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleUpdateAbstractStatus(abs.id, "revision")} className="text-orange-600" title="Pedir Revisão">
                              <AlertCircle className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setRejectionDialog({ open: true, abstractId: abs.id })} className="text-red-600" title="Rejeitar">
                              <XCircle className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteAbstract(abs.id)} className="text-gray-600" title="Eliminar">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {abstracts.length === 0 && <div className="p-8 text-center text-gray-500">{t.admin.noSubmissions}</div>}
            </div>
          </TabsContent>

          {/* Presentations Tab */}
          <TabsContent value="presentations">
            <div className="bg-white shadow-sm overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="font-heading text-lg font-bold text-secondary">{t.admin.presentations}</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchData}>{t.admin.refresh}</Button>
                </div>
              </div>
              
              {/* Link para enviar aos autores */}
              <div className="p-4 bg-blue-50 border-b">
                <p className="text-sm text-blue-800">
                  <strong>Link para enviar aos autores:</strong>{" "}
                  <code className="bg-blue-100 px-2 py-1 rounded text-xs">
                    {window.location.origin}/submeter-ficheiro
                  </code>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="ml-2 text-blue-600"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/submeter-ficheiro`);
                      toast.success("Link copiado!");
                    }}
                  >
                    Copiar
                  </Button>
                </p>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.admin.submissionId}</TableHead>
                      <TableHead>{t.admin.type}</TableHead>
                      <TableHead>{t.admin.name}</TableHead>
                      <TableHead>{t.admin.email}</TableHead>
                      <TableHead>{t.admin.fileName}</TableHead>
                      <TableHead>{t.admin.date}</TableHead>
                      <TableHead>{t.admin.actions}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {presentationUploads.map((upload) => (
                      <TableRow key={upload.id}>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">{upload.submission_id}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={upload.presentation_type === "oral" ? "bg-blue-500" : "bg-purple-500"}>
                            {presentationTypeLabels[upload.presentation_type]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{upload.submitter_name}</TableCell>
                        <TableCell className="text-sm text-gray-500">{upload.submitter_email}</TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {upload.filename}
                          <div className="text-gray-400">
                            {upload.file_size ? `${(upload.file_size / 1024 / 1024).toFixed(2)} MB` : ""}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {upload.created_at ? new Date(upload.created_at).toLocaleDateString("pt-PT") : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDownloadPresentation(upload.id, upload.submission_id, upload.presentation_type)} 
                              className="text-blue-600"
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeletePresentation(upload.id)} 
                              className="text-red-600"
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {presentationUploads.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  {t.admin.noPresentations}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Payment Proof Dialog */}
      <Dialog open={showProofDialog} onOpenChange={setShowProofDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comprovativo</DialogTitle>
            <DialogDescription>{selectedProof?.filename}</DialogDescription>
          </DialogHeader>
          {selectedProof && (
            <div className="mt-4">
              {selectedProof.filename?.endsWith(".pdf") ? (
                <div className="text-center p-8 bg-gray-100">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <a href={`data:application/pdf;base64,${selectedProof.data}`} download={selectedProof.filename} className="text-primary hover:underline">
                    <Download className="h-4 w-4 inline mr-2" />Descarregar PDF
                  </a>
                </div>
              ) : (
                <img src={`data:image/*;base64,${selectedProof.data}`} alt="Comprovativo" className="max-w-full h-auto" />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Abstract Details Dialog */}
      <Dialog open={showAbstractDialog} onOpenChange={setShowAbstractDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-primary font-bold">{selectedAbstract?.sequential_id || ""}</span>
              Detalhes do Resumo
            </DialogTitle>
          </DialogHeader>
          {selectedAbstract && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-sm">Tipo de Apresentação</Label>
                  <p className="font-medium">{typeLabels[selectedAbstract.submission_type]}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Eixo Temático</Label>
                  <p className="font-medium text-sm">{selectedAbstract.thematic_area}</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded">
                <Label className="text-gray-500 text-sm">Título (Português)</Label>
                <p className="font-bold text-lg">{selectedAbstract.title_pt || selectedAbstract.title}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded">
                <Label className="text-gray-500 text-sm">Title (English)</Label>
                <p className="font-bold">{selectedAbstract.title_en}</p>
              </div>
              
              <div>
                <Label className="text-gray-500 text-sm">Autores e Afiliações</Label>
                <div className="space-y-2 mt-1">
                  {selectedAbstract.authors?.map((author, i) => {
                    const affiliations = selectedAbstract.affiliations || [];
                    const authorAffils = author.affiliation_ids?.map(id => 
                      affiliations.find(a => a.id === id)?.name
                    ).filter(Boolean).join("; ") || author.affiliation || "";
                    return (
                      <div key={i} className="bg-gray-50 p-3">
                        <p className="font-medium">{author.name}</p>
                        <p className="text-sm text-gray-600">{authorAffils}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500 text-sm">Resumo (Português)</Label>
                <p className="text-sm bg-gray-50 p-3 whitespace-pre-wrap">{selectedAbstract.abstract_pt || selectedAbstract.abstract}</p>
              </div>
              
              {selectedAbstract.abstract_en && (
                <div>
                  <Label className="text-gray-500 text-sm">Abstract (English)</Label>
                  <p className="text-sm bg-gray-50 p-3 whitespace-pre-wrap">{selectedAbstract.abstract_en}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-sm">Palavras-chave (PT)</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(selectedAbstract.keywords_pt || selectedAbstract.keywords || []).map((kw, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Keywords (EN)</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(selectedAbstract.keywords_en || []).map((kw, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              
              {selectedAbstract.references && (
                <div>
                  <Label className="text-gray-500 text-sm">Referências Bibliográficas</Label>
                  <p className="text-sm bg-gray-50 p-3 whitespace-pre-wrap">{selectedAbstract.references}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 border-t pt-4">
                <div>
                  <Label className="text-gray-500 text-sm">Autor Responsável</Label>
                  <p className="font-medium">{selectedAbstract.corresponding_author_name}</p>
                  <p className="text-sm text-gray-600">{selectedAbstract.corresponding_author_email || selectedAbstract.corresponding_email}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Autor Apresentador</Label>
                  <p className="font-medium">{selectedAbstract.presenting_author_name}</p>
                  <p className="text-sm text-gray-600">{selectedAbstract.presenting_author_email}</p>
                </div>
              </div>
              
              <div className="flex gap-4 border-t pt-4">
                <div>
                  <Label className="text-gray-500 text-sm">Estado</Label>
                  <Badge className={statusColors[selectedAbstract.status]}>{statusLabels[selectedAbstract.status]}</Badge>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Gestor</Label>
                  <p className="font-medium">{assignees[selectedAbstract.assigned_to]?.name || "-"}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Revisor</Label>
                  <p className="font-medium">{reviewers[selectedAbstract.reviewer]?.name || "-"}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <Button onClick={() => handleDownloadWord(selectedAbstract.id, selectedAbstract.sequential_id)} className="w-full bg-primary text-primary-foreground hover:bg-primary-hover">
                  <Download className="h-4 w-4 mr-2" />
                  Descarregar Documento Word ({selectedAbstract.sequential_id}_resumo.docx)
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Reasons Dialog */}
      <Dialog open={rejectionDialog.open} onOpenChange={(open) => { if (!open) { setRejectionDialog({ open: false, abstractId: null }); setRejectionReasons(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Rejeitar Resumo</DialogTitle>
            <DialogDescription>Indique os motivos da rejeição (serão incluídos no email ao autor).</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <textarea
              value={rejectionReasons}
              onChange={(e) => setRejectionReasons(e.target.value)}
              placeholder="Motivos da rejeição..."
              className="w-full h-32 p-3 border rounded-md text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              data-testid="rejection-reasons-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setRejectionDialog({ open: false, abstractId: null }); setRejectionReasons(""); }} data-testid="rejection-cancel-btn">
              Cancelar
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleRejectWithReasons} data-testid="rejection-confirm-btn">
              Confirmar Rejeição
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const PresentationUploadPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    submission_id: "",
    submitter_name: "",
    submitter_email: "",
    presentation_type: ""
  });
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      if (!fileName.endsWith('.ppt') && !fileName.endsWith('.pptx')) {
        toast.error("Formato inválido. Apenas ficheiros .ppt e .pptx são aceites.");
        e.target.value = "";
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.submission_id.trim()) {
      toast.error("Por favor, introduza o ID da submissão");
      return;
    }
    if (!formData.submitter_name.trim()) {
      toast.error("Por favor, introduza o nome do autor correspondente");
      return;
    }
    if (!formData.submitter_email.trim()) {
      toast.error("Por favor, introduza o email do autor correspondente");
      return;
    }
    if (!formData.presentation_type) {
      toast.error("Por favor, selecione o tipo de apresentação");
      return;
    }
    if (!file) {
      toast.error("Por favor, selecione um ficheiro");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append("submission_id", formData.submission_id.trim());
      submitData.append("submitter_name", formData.submitter_name.trim());
      submitData.append("submitter_email", formData.submitter_email.trim());
      submitData.append("presentation_type", formData.presentation_type);
      submitData.append("presentation_file", file);

      const response = await axios.post(`${API}/upload-presentation`, submitData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success(response.data.message);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao submeter ficheiro. Por favor, tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 max-w-md w-full text-center border border-gray-200">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-secondary mb-4">
            {t.upload.success}
          </h1>
          <p className="text-gray-600 mb-6">
            CIAFD 2026
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({ submission_id: "", submitter_name: "", submitter_email: "", presentation_type: "" });
              setFile(null);
            }}
            variant="outline"
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          >
            {t.upload.submitBtn}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_7da1e8fe-490e-4a3e-9f5f-ce553b84d2ec/artifacts/msg5ulso_CIAFD%202026.png"
              alt="CIAFD 2026"
              className="h-24"
            />
          </div>
          <h1 className="font-heading text-3xl font-bold text-secondary mb-2">
            {t.upload.title}
          </h1>
          <p className="text-gray-600">
            CIAFD 2026
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 p-8">
          <div className="space-y-6">
            {/* Submission ID */}
            <div>
              <Label htmlFor="submission_id" className="text-secondary font-medium">
                {t.upload.submissionId}
              </Label>
              <Input
                id="submission_id"
                name="submission_id"
                value={formData.submission_id}
                onChange={handleInputChange}
                placeholder="Ex: S1, S2, S3..."
                className="mt-1"
                required
              />
            </div>

            {/* Submitter Name */}
            <div>
              <Label htmlFor="submitter_name" className="text-secondary font-medium">
                {t.upload.name}
              </Label>
              <Input
                id="submitter_name"
                name="submitter_name"
                value={formData.submitter_name}
                onChange={handleInputChange}
                placeholder={t.submissions.namePlaceholder}
                className="mt-1"
                required
              />
            </div>

            {/* Submitter Email */}
            <div>
              <Label htmlFor="submitter_email" className="text-secondary font-medium">
                {t.upload.email}
              </Label>
              <Input
                id="submitter_email"
                name="submitter_email"
                type="email"
                value={formData.submitter_email}
                onChange={handleInputChange}
                placeholder={t.submissions.emailPlaceholder}
                className="mt-1"
                required
              />
            </div>

            {/* Presentation Type */}
            <div>
              <Label className="text-secondary font-medium">{t.upload.presentationType}</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div
                  className={`p-4 border-2 cursor-pointer transition-colors text-center ${
                    formData.presentation_type === "oral" 
                      ? "border-primary bg-primary/5" 
                      : "border-gray-200 hover:border-primary/50"
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, presentation_type: "oral" }))}
                >
                  <Mic className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <span className="font-semibold text-secondary text-sm">{t.upload.oralComm}</span>
                </div>
                <div
                  className={`p-4 border-2 cursor-pointer transition-colors text-center ${
                    formData.presentation_type === "poster" 
                      ? "border-primary bg-primary/5" 
                      : "border-gray-200 hover:border-primary/50"
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, presentation_type: "poster" }))}
                >
                  <ClipboardList className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <span className="font-semibold text-secondary text-sm">{t.upload.poster}</span>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <Label htmlFor="presentation_file" className="text-secondary font-medium">
                {t.upload.file}
              </Label>
              <div className="mt-2">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="presentation_file"
                    accept=".ppt,.pptx"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="presentation_file" className="cursor-pointer">
                    <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                    {file ? (
                      <div>
                        <p className="text-primary font-medium">{file.name}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-600 font-medium">{t.upload.uploadBtn}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {t.upload.fileTypes}
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-primary-foreground hover:bg-primary-hover font-bold uppercase tracking-widest py-6"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin mr-2">...</span>
                  {t.upload.submitting}
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {t.upload.submitBtn}
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-500 mt-6">
          Em caso de dúvidas, contacte-nos através de{" "}
          <a href="mailto:ciafd.ipbeja@gmail.com" className="text-primary hover:underline">
            ciafd.ipbeja@gmail.com
          </a>
        </p>
      </div>
    </div>
  );
};

// Home component with page view tracking
const Home = () => {
  useEffect(() => {
    // Track page view
    axios.post(`${API}/track/pageview?page=/`).catch(() => {});
  }, []);

  return (
    <div className="overflow-x-hidden">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <CommitteesSection />
      <ProgramSection />
      <SubmissionsSection />
      <CelebrationSection />
      <PricingSection />
      <RegistrationSection />
      <MastersPromoSection />
      <SponsorsSection />
      <Footer />
    </div>
  );
};

// App
function App() {
  return (
    <div className="min-h-screen">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/submeter-ficheiro" element={<PresentationUploadPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
